import {
  S as Signal,
  M as MColor,
  a as MeshColorTexture,
  b as MeshColorTextureFormat,
  c as MeshColorTextureType,
  d as MeshColorSerializer,
  e as MeshColorTextureSerializer,
  R as RGBToOklab,
  g as getMeshColorFacesFromVertexSelection,
  f as MeshColorSkinned,
  O as OklabToRGB,
  s as strFromU8,
  h as strToU8,
  z as zipSync
} from "./three-js-mesh-colors.internal.js";
export {
  i as MeshColor,
  j as MeshColorInternal,
  k as estimateMeshColorPointResolution,
  l as estimateMeshColorTextureResolution,
  m as getFaceColorSample,
  n as mergeMeshColorAndTextures,
  o as mixinMeshColor
} from "./three-js-mesh-colors.internal.js";
import {
  Color,
  Vector3,
  ShaderMaterial,
  Vector2,
  Mesh,
  SkinnedMesh,
  Bone,
  ShaderChunk,
  Vector4,
  Scene,
  Box3,
  Sphere,
  Matrix3,
  MeshBasicMaterial,
  BufferAttribute,
  WebGLRenderer,
  PMREMGenerator,
  DataTexture,
  Object3D,
  Matrix4,
  WebGLRenderTarget,
  LinearFilter,
  Float32BufferAttribute,
  BufferGeometry,
  OrthographicCamera,
  UniformsUtils,
  UniformsLib
} from "three";
import {
  BufferGeometryComputations,
  BufferGeometrySerializer,
  BufferGeometryUtils as BufferGeometryUtils$1,
  OBJMTLExporter,
  getCameraRay,
  getObjectsIntersections,
  VertexSelector
} from "./three-js-extra.module.js";
import { BufferGeometryUtils as BufferGeometryUtils$2 } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { Service, ModuleWorker } from "./blob-worker.module.js";
import {
  MeshWorldNormalMaterial,
  EdgeSplitModifier
} from "./three-js-extra-public.module.js";
import "three/examples/jsm/loaders/GLTFLoader.js";
import "three/examples/jsm/exporters/GLTFExporter.js";
import {
  EffectComposer,
  RenderPass,
  ShaderPass
} from "./three-js-post-processing-pipeline.module.js";
import { BufferGeometryUtils } from "three/examples/jsm/utils/BufferGeometryUtils";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
import { STLBakerMaterial } from "./three-js-baking.module.js";
import {
  SceneMaterialSwitcher,
  MaterialCorrespondancePolicy
} from "./three-js-material-switcher.module.js";
import { computeGeometryMeanCurvatures } from "./three-js-mesh-attributes.module.js";
import { AsyncVertexBaker } from "./three-js-ao-compute.module.js";

/**
 *  @template T
 */
class Observable {
  #value;
  #changed = new Signal();

  /**
   * @param {T} value Initial value
   */
  constructor(value) {
    this.#value = value;
  }

  set value(value) {
    if (value === this.#value) return;
    this.#value = value;
    this.#changed.emit(this.#value);
  }

  get value() {
    return this.#value;
  }

  get changed() {
    return this.#changed.readonly();
  }

  /**
   * Subscribes the given function to the observable and returns an unsubscribing function.
   * Calls the given function with the current value of the observable.
   * @param {(arg:T)=>void} slot function called whenever the observable changes
   * @returns {function} unsubscribing function
   */
  subscribe(slot) {
    slot(this.#value);
    return this.#changed.on(slot);
  }

  /**
   * Subscribes the given function to the observable and returns an unsubscribing function.
   * @param {(arg:T)=>void} slot function called whenever the observable changes
   * @returns {function} unsubscribing function
   */
  onChanged(slot) {
    return this.#changed.on(slot);
  }

  readonly() {
    let self = this;
    return {
      get changed() {
        return self.changed;
      },
      get value() {
        return self.value;
      },
      subscribe: (slot) => {
        return self.subscribe(slot);
      }
    };
  }
}

// Optimization variables to avoid excessive object allocation
new Color();
const tmpCol1 = new Vector3();
const tmpCol2 = new Vector3();
const tmpCol3 = new Vector3();
function getColorFromOkLabTriplet(colorTriplet, level, mixedColor) {
  if (level <= colorTriplet.lowColorLevel) {
    OklabToRGB(colorTriplet.lowColor, tmpCol1);
    mixedColor.setRGBA(
      tmpCol1.x,
      tmpCol1.y,
      tmpCol1.z,
      colorTriplet.lowColorAlpha
    );
  } else if (level >= colorTriplet.highColorLevel) {
    OklabToRGB(colorTriplet.highColor, tmpCol1);
    mixedColor.setRGBA(
      tmpCol1.x,
      tmpCol1.y,
      tmpCol1.z,
      colorTriplet.highColorAlpha
    );
  } else if (level <= colorTriplet.mediumColorLevel) {
    let sumBar = colorTriplet.lowColorAlpha + colorTriplet.mediumColorAlpha;
    let levelLowBar = sumBar > 0 ? colorTriplet.lowColorAlpha / sumBar : 0;
    let levelMediumBar =
      sumBar > 0 ? colorTriplet.mediumColorAlpha / sumBar : 0;
    let levelFactBar = levelMediumBar - levelLowBar;
    let mixFact =
      (level - colorTriplet.lowColorLevel) /
      (colorTriplet.mediumColorLevel - colorTriplet.lowColorLevel);
    let alphaMixFact =
      levelFactBar > 0
        ? (1.0 - levelFactBar * mixFact) * mixFact + levelFactBar * mixFact
        : (1.0 + levelFactBar) * mixFact - levelFactBar * mixFact * mixFact;
    tmpCol3.lerpVectors(
      colorTriplet.lowColor,
      colorTriplet.mediumColor,
      alphaMixFact
    );
    OklabToRGB(tmpCol3, tmpCol1);
    mixedColor.setRGB(tmpCol1.x, tmpCol1.y, tmpCol1.z);
    mixedColor.a =
      (1.0 - mixFact) * colorTriplet.lowColorAlpha +
      mixFact * colorTriplet.mediumColorAlpha;
  } else {
    let sumBar = colorTriplet.mediumColorAlpha + colorTriplet.highColorAlpha;
    let levelMediumBar =
      sumBar > 0 ? colorTriplet.mediumColorAlpha / sumBar : 0;
    let levelHighBar = sumBar > 0 ? colorTriplet.highColorAlpha / sumBar : 0;
    let levelFactBar = levelHighBar - levelMediumBar;
    let mixFact =
      (level - colorTriplet.mediumColorLevel) /
      (colorTriplet.highColorLevel - colorTriplet.mediumColorLevel);
    let alphaMixFact =
      levelFactBar > 0
        ? (1.0 - levelFactBar * mixFact) * mixFact + levelFactBar * mixFact
        : (1.0 + levelFactBar) * mixFact - levelFactBar * mixFact * mixFact;
    tmpCol3.lerpVectors(
      colorTriplet.mediumColor,
      colorTriplet.highColor,
      alphaMixFact
    );
    OklabToRGB(tmpCol3, tmpCol1);
    mixedColor.setRGB(tmpCol1.x, tmpCol1.y, tmpCol1.z);
    mixedColor.a =
      (1.0 - mixFact) * colorTriplet.mediumColorAlpha +
      mixFact * colorTriplet.highColorAlpha;
  }
}
function getColorFromTriplet(colorTriplet, level, mixedColor) {
  if (level <= colorTriplet.lowColorLevel) {
    mixedColor.setRGBA(
      colorTriplet.lowColor.r,
      colorTriplet.lowColor.g,
      colorTriplet.lowColor.b,
      colorTriplet.lowColorAlpha
    );
  } else if (level >= colorTriplet.highColorLevel) {
    mixedColor.setRGBA(
      colorTriplet.highColor.r,
      colorTriplet.highColor.g,
      colorTriplet.highColor.b,
      colorTriplet.highColorAlpha
    );
  } else if (level <= colorTriplet.mediumColorLevel) {
    //tmpColAlphaPremult.copy(colorTriplet.mediumColor);
    //mediumAlphaPremult.multiplyScalar(colorTriplet.mediumColorAlpha);
    // TODO not bad but do better because the formula should take the initial distances between
    // level and lowLevel and level and mediumLevel
    let sumBar = colorTriplet.lowColorAlpha + colorTriplet.mediumColorAlpha;
    let levelLowBar = sumBar > 0 ? colorTriplet.lowColorAlpha / sumBar : 0;
    let levelMediumBar =
      sumBar > 0 ? colorTriplet.mediumColorAlpha / sumBar : 0;
    let levelFactBar = levelMediumBar - levelLowBar; //
    /*let alphaLevel = (1.0-colorTriplet.mediumColorAlpha)*colorTriplet.lowColorLevel
                + colorTriplet.mediumColorAlpha*level;
        alphaLevel = (1.0-colorTriplet.lowColorAlpha)*colorTriplet.mediumColorLevel
                + colorTriplet.lowColorAlpha*alphaLevel;*/
    let mixFact =
      (level - colorTriplet.lowColorLevel) /
      (colorTriplet.mediumColorLevel - colorTriplet.lowColorLevel);
    let alphaMixFact =
      levelFactBar > 0
        ? (1.0 - levelFactBar * mixFact) * mixFact + levelFactBar * mixFact
        : (1.0 + levelFactBar) * mixFact - levelFactBar * mixFact * mixFact;
    //(alphaLevel - colorTriplet.lowColorLevel)/( colorTriplet.mediumColorLevel - colorTriplet.lowColorLevel);
    //mixedColor.setRGB(colorTriplet.lowColor.r,colorTriplet.lowColor.g,colorTriplet.lowColor.b)//.multiplyScalar(colorTriplet.lowColorAlpha);
    //.lerp(mediumAlphaPremult,mixFact);
    tmpCol1.set(
      colorTriplet.lowColor.r,
      colorTriplet.lowColor.g,
      colorTriplet.lowColor.b
    );
    tmpCol2.set(
      colorTriplet.mediumColor.r,
      colorTriplet.mediumColor.g,
      colorTriplet.mediumColor.b
    );
    tmpCol3.lerpVectors(tmpCol1, tmpCol2, alphaMixFact);
    mixedColor.setRGB(tmpCol3.x, tmpCol3.y, tmpCol3.z);
    mixedColor.a =
      (1.0 - mixFact) * colorTriplet.lowColorAlpha +
      mixFact * colorTriplet.mediumColorAlpha;
  } else {
    //tmpColAlphaPremult.copy(colorTriplet.highColor);
    //highAlphaPremult.multiplyScalar(colorTriplet.highColorAlpha);
    // TODO not bad but do better because the formula should take the initial distances between
    // level and lowLevel and level and mediumLevel
    let sumBar = colorTriplet.mediumColorAlpha + colorTriplet.highColorAlpha;
    let levelMediumBar =
      sumBar > 0 ? colorTriplet.mediumColorAlpha / sumBar : 0;
    let levelHighBar = sumBar > 0 ? colorTriplet.highColorAlpha / sumBar : 0;
    let levelFactBar = levelHighBar - levelMediumBar;
    /*let alphaLevel = (1.0-colorTriplet.highColorAlpha)*colorTriplet.mediumColorLevel
                + colorTriplet.highColorAlpha*level;
        alphaLevel = (1.0-colorTriplet.mediumColorAlpha)*colorTriplet.highColorLevel
                + colorTriplet.mediumColorAlpha*alphaLevel;*/
    let mixFact =
      (level - colorTriplet.mediumColorLevel) /
      (colorTriplet.highColorLevel - colorTriplet.mediumColorLevel);
    let alphaMixFact =
      levelFactBar > 0
        ? (1.0 - levelFactBar * mixFact) * mixFact + levelFactBar * mixFact
        : (1.0 + levelFactBar) * mixFact - levelFactBar * mixFact * mixFact;
    //(alphaLevel - colorTriplet.mediumColorLevel)/( colorTriplet.highColorLevel - colorTriplet.mediumColorLevel);
    //mixedColor.setRGB(colorTriplet.mediumColor.r,colorTriplet.mediumColor.g,colorTriplet.mediumColor.b)//.multiplyScalar(colorTriplet.mediumColorAlpha);
    //.lerp(highAlphaPremult,mixFact);
    tmpCol1.set(
      colorTriplet.mediumColor.r,
      colorTriplet.mediumColor.g,
      colorTriplet.mediumColor.b
    );
    tmpCol2.set(
      colorTriplet.highColor.r,
      colorTriplet.highColor.g,
      colorTriplet.highColor.b
    );
    tmpCol3.lerpVectors(tmpCol1, tmpCol2, alphaMixFact);
    mixedColor.setRGB(tmpCol3.x, tmpCol3.y, tmpCol3.z);
    mixedColor.a =
      (1.0 - mixFact) * colorTriplet.mediumColorAlpha +
      mixFact * colorTriplet.highColorAlpha;
  }
}
function meshColorColoring(meshColor, selectedPoints, color, alpha = 1.0) {
  let ptMColor = new MColor();
  for (let pt of selectedPoints) {
    let meshPtColor = meshColor.getSampleColor(pt.index);
    //mix between already stored color and new color depending on alpha and selection alpha
    //meshPtColor.lerpColors(meshPtColor,ptMColor,ptColor.alpha*pt.alpha);
    ptMColor.setRGBA(color.r, color.g, color.b, 1.0);
    let mix_fact = Math.max(0.0, Math.min(1.0, pt.alpha * alpha));
    meshPtColor.lerpColors(meshPtColor, ptMColor, mix_fact);
  }
}
function meshColorOkLabSurfaceGradientColoring(
  meshColor,
  selectedPoints,
  surfaceGradientAttributeName,
  colorTriplet
) {
  let colorSampleGeometry = meshColor.getColorSampleGeometry();
  if (!colorSampleGeometry) {
    console.log("[MeshColor] Cannot paint non initialized MeshColor.");
    return;
  }
  let gradientAttribute = colorSampleGeometry.getAttribute(
    surfaceGradientAttributeName
  );
  if (!gradientAttribute) {
    console.log(
      "Surface gradient attribute: " +
        surfaceGradientAttributeName +
        " not found"
    );
    return;
  }
  let ptMColor = new MColor();
  let mixedColor = new MColor();
  for (let pt of selectedPoints) {
    //surface gradient treated as level
    let surfaceGradient = gradientAttribute.getX(pt.index);
    //get color from gradient
    getColorFromOkLabTriplet(colorTriplet, surfaceGradient, mixedColor);
    ptMColor.setRGBA(
      mixedColor.r,
      mixedColor.g,
      mixedColor.b,
      Math.max(0.0, Math.min(1.0, mixedColor.a * pt.alpha))
    );
    let meshPtColor = meshColor.getSampleColor(pt.index);
    // Alpha over blending
    ptMColor.alphaBlendColor(meshPtColor);
    meshPtColor.copy(ptMColor);
  }
}
function normalizeVertexSelection(selection) {
  if (selection instanceof Map) {
    const arr = [];
    for (const [index, data] of selection) {
      arr.push({
        index,
        alpha: data && typeof data.alpha === "number" ? data.alpha : 1
      });
    }
    return arr;
  }
  return selection;
}

function meshColorSurfaceGradientColoring(
  meshColor,
  selectedPoints,
  surfaceGradientAttributeName,
  colorTriplet
) {
  let colorSampleGeometry = meshColor.getColorSampleGeometry();
  if (!colorSampleGeometry) {
    console.log("[MeshColor] Cannot paint non initialized MeshColor.");
    return;
  }
  let gradientAttribute = colorSampleGeometry.getAttribute(
    surfaceGradientAttributeName
  );
  if (!gradientAttribute) {
    console.log(
      "Surface gradient attribute: " +
        surfaceGradientAttributeName +
        " not found"
    );
    return;
  }
  let ptMColor = new MColor();
  let mixedColor = new MColor();
  let points = normalizeVertexSelection(selectedPoints);
  for (let pt of points) {
    //surface gradient treated as level
    let surfaceGradient = gradientAttribute.getX(pt.index);
    //get color from gradient
    getColorFromTriplet(colorTriplet, surfaceGradient, mixedColor);
    ptMColor.setRGBA(
      mixedColor.r,
      mixedColor.g,
      mixedColor.b,
      Math.max(0.0, Math.min(1.0, mixedColor.a * pt.alpha))
    );
    let meshPtColor = meshColor.getSampleColor(pt.index);
    // Alpha over blending
    ptMColor.alphaBlendColor(meshPtColor);
    meshPtColor.copy(ptMColor);
  }
}
function meshColorSurfaceColoring(
  meshColor,
  selectedPoints,
  surfaceGradientAttributeName,
  color,
  surfaceThrehold = 0.0,
  invertAttribute = false,
  alpha = 1.0
) {
  let colorSampleGeometry = meshColor.getColorSampleGeometry();
  if (!colorSampleGeometry) {
    console.warn("Surface gradient : " + "sampleGeometry missing");
    return;
  }
  let gradientAttribute = colorSampleGeometry.getAttribute(
    surfaceGradientAttributeName
  );
  if (!gradientAttribute) {
    console.warn(
      "Surface gradient attribute: " +
        surfaceGradientAttributeName +
        " not found"
    );
    return;
  }
  let ptMColor = new MColor();
  for (let pt of selectedPoints) {
    //surface gradient treated as level
    let surfaceGradient = gradientAttribute.getX(pt.index);
    if (invertAttribute) surfaceGradient = 1.0 - surfaceGradient;
    if (surfaceGradient < surfaceThrehold) surfaceGradient = 0.0;
    //get color from gradient
    ptMColor.setRGBA(color.r, color.g, color.b, 1.0);
    let meshPtColor = meshColor.getSampleColor(pt.index);
    //mix between already stored color and new color depending on color alpha AND
    //selection alpha
    let mix_fact = Math.max(
      0.0,
      Math.min(1.0, alpha * pt.alpha * surfaceGradient)
    );
    meshPtColor.lerpColors(meshPtColor, ptMColor, mix_fact);
  }
}

function base64ToArrayBuffer(base64) {
  let binaryString = atob(base64);
  let bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}
function arrayBufferToBase64(raw) {
  let binaryString = [];
  for (var i = 0; i < raw.length; i++) {
    binaryString.push(String.fromCharCode(raw[i]));
  }
  return btoa(binaryString.join(""));
}
function stringFromArrayBuffer(raw) {
  return strFromU8(raw);
}
function zipFiles(files) {
  let zip = {};
  for (let file of files) {
    if (file.base64) {
      if (typeof file.raw != "string") {
        throw "[MeshColor] zip: base64 raw data must be a string";
      }
      zip[file.name] = base64ToArrayBuffer(file.raw);
    } else if (file.raw instanceof Uint8Array) zip[file.name] = file.raw;
    else zip[file.name] = strToU8(file.raw);
  }
  return new Blob([zipSync(zip)]);
}
/**
 *  Convert a CubeTexture into a simple Texture with special UV mapping
 */
function CubeTextureToTextureCubeUV(cubeMap) {
  var map_renderer = new WebGLRenderer();
  map_renderer.setClearColor(0xffffff);
  map_renderer.setSize(1024, 1024);
  var pmremGenerator = new PMREMGenerator(map_renderer);
  var hdrCubeRenderTarget = pmremGenerator.fromCubemap(cubeMap);
  // on the GPU, result is currently in hdrCubeRenderTarget.texture, but we will transfert
  // it to a DataTexture so that we can easily dispose it if we want to.
  let buffer = new Uint8Array(
    hdrCubeRenderTarget.width * hdrCubeRenderTarget.height * 4
  );
  map_renderer.readRenderTargetPixels(
    hdrCubeRenderTarget,
    0,
    0,
    hdrCubeRenderTarget.width,
    hdrCubeRenderTarget.height,
    buffer
  );
  var dataTextureCubeUV = new DataTexture(
    buffer,
    hdrCubeRenderTarget.width,
    hdrCubeRenderTarget.height,
    hdrCubeRenderTarget.texture.format,
    hdrCubeRenderTarget.texture.type,
    hdrCubeRenderTarget.texture.mapping,
    hdrCubeRenderTarget.texture.wrapS,
    hdrCubeRenderTarget.texture.wrapT,
    hdrCubeRenderTarget.texture.magFilter,
    hdrCubeRenderTarget.texture.minFilter,
    hdrCubeRenderTarget.texture.anisotropy,
    hdrCubeRenderTarget.texture.encoding
  );
  // dataTextureCubeUV.flipY = hdrCubeRenderTarget.texture.flipY;
  dataTextureCubeUV.unpackAlignment =
    hdrCubeRenderTarget.texture.unpackAlignment;
  dataTextureCubeUV.needsUpdate = true;
  pmremGenerator.dispose();
  return dataTextureCubeUV;
}
function generatePointHash(point, normal = undefined, epsAccuracy = 7) {
  if (normal === undefined) {
    return (
      point.x.toFixed(epsAccuracy) +
      "," +
      point.y.toFixed(epsAccuracy) +
      "," +
      point.z.toFixed(epsAccuracy)
    );
  } else {
    return (
      point.x.toFixed(epsAccuracy) +
      "," +
      point.y.toFixed(epsAccuracy) +
      "," +
      point.z.toFixed(epsAccuracy) +
      normal.x.toFixed(epsAccuracy) +
      "," +
      normal.y.toFixed(epsAccuracy) +
      "," +
      normal.z.toFixed(epsAccuracy)
    );
  }
}
// used for non indexed geometries like STL models
function splitAndCreaseGeometry(non_indexed_geometry) {
  non_indexed_geometry.deleteAttribute("normal");
  non_indexed_geometry = BufferGeometryUtils$2.mergeVertices(
    non_indexed_geometry,
    1e-7
  );
  let geometries =
    BufferGeometryComputations.splitIntoConnectedParts(non_indexed_geometry);
  for (let i = 0; i < geometries.length; ++i) {
    let geometry = geometries[i];
    geometry.computeVertexNormals();
    const edgeSplitModifier = new EdgeSplitModifier();
    //Crease geometry
    geometries[i] = edgeSplitModifier.modify(
      geometry,
      (30 * Math.PI) / 180,
      false
    );
  }
  return geometries;
}
/**
 * Adjust curvatures so that they fit into [0;1]
 * @param {BufferGeometry[]} geometries
 */
function adjustCurvatureAttribute(geometries) {
  // Curvature are normalized per geometry
  for (let geometry of geometries) {
    let min_curv = Number.MAX_VALUE;
    let max_curv = -Number.MAX_VALUE;
    let curv_average = 0;
    let curv_count = 0;
    // Compute mean
    const meanCurvature = geometry.getAttribute("meanCurvature");
    for (let i = 0; i < meanCurvature.count; i++) {
      let curvature = meanCurvature.getX(i);
      if (!isNaN(curvature)) {
        curv_average += curvature;
        curv_count++;
      }
    }
    // compute standard deviation
    curv_average /= curv_count;
    let curv_std_dev = 0;
    for (let i = 0; i < meanCurvature.count; i++) {
      let curvature = meanCurvature.getX(i);
      if (!isNaN(curvature))
        curv_std_dev += (curvature - curv_average) * (curvature - curv_average);
    }
    curv_std_dev /= curv_count;
    curv_std_dev = Math.sqrt(curv_std_dev);
    // compute min and max curvatures by ignonring  outliers
    // that step out the [mean - 2.0*std_dev : mean + 2.0*std_dev] interval
    let outlier_thres = 2.0 * curv_std_dev;
    // recompute average and standard deviation without considering outliers
    let new_curv_average = 0;
    let new_curv_count = 0;
    for (let i = 0; i < meanCurvature.count; i++) {
      const curvature = meanCurvature.getX(i);
      if (
        !isNaN(curvature) &&
        Math.abs(curvature - curv_average) < outlier_thres
      ) {
        new_curv_average += curvature;
        new_curv_count++;
      }
    }
    new_curv_average /= new_curv_count;
    curv_average = new_curv_average;
    // compute new standard deviation
    let new_curv_std_dev = 0;
    for (let i = 0; i < meanCurvature.count; i++) {
      const curvature = meanCurvature.getX(i);
      if (
        !isNaN(curvature) &&
        Math.abs(curvature - curv_average) < outlier_thres
      )
        new_curv_std_dev +=
          (curvature - curv_average) * (curvature - curv_average);
    }
    new_curv_std_dev /= new_curv_count;
    new_curv_std_dev = Math.sqrt(new_curv_std_dev);
    curv_std_dev = new_curv_std_dev;
    // Assign new outliers threshold
    // Removing values that are still too extreme
    outlier_thres = 4.0 * curv_std_dev;
    // Compute min and max curvatures without considering outliers
    for (let i = 0; i < meanCurvature.count; i++) {
      let curvature = meanCurvature.getX(i);
      if (
        !isNaN(curvature) &&
        Math.abs(curvature - curv_average) < outlier_thres
      ) {
        min_curv = Math.min(min_curv, curvature);
        max_curv = Math.max(max_curv, curvature);
      }
    }
    // normalize curvatures
    for (let i = 0; i < meanCurvature.count; ++i) {
      let curvature = meanCurvature.getX(i);
      if (curvature - curv_average > outlier_thres) {
        curvature = max_curv;
      } else if (curvature - curv_average < -outlier_thres) {
        curvature = min_curv;
      }
      curvature = (curvature - min_curv) / (max_curv - min_curv);
      meanCurvature.setX(i, Math.max(0, Math.min(1, curvature)));
    }
  }
}
// Computes the object Aligned-Axis-Bounding-Box
function getAABB(objects) {
  const box = new Box3();
  box.makeEmpty();
  objects.forEach((object) => box.expandByObject(object));
  return box;
}

class ComputeService extends Service {
  _postMessage(e, transfer) {
    return super._postMessage(e, transfer);
  }
  _onWorkerMessage(e) {
    const job = this._popJob(e.data.processId);
    switch (e.data.cmd) {
      case "parsedSTL": {
        job.resolve(BufferGeometrySerializer.fromWorkerData(e.data.geometry));
        break;
      }
      case "computedAdjustedModelColorAccuracy": {
        job.resolve(e.data.accuracy);
        break;
      }
      case "computedModelMeshColors": {
        //deserialize the worker response
        let meshesColor = new Array();
        for (let meshColor of e.data.meshesColor) {
          meshesColor.push(MeshColorSerializer.fromWorkerData(meshColor));
          MeshColorSerializer.disposeWorkerData(meshColor);
        }
        // send back the result to the caller of the promise
        job.resolve(meshesColor);
        break;
      }
      case "computedMeshColorsTextures": {
        let meshesColor = new Array();
        //console.time("meshcolor reconstruct");
        for (let meshColor of e.data.meshesColor) {
          meshesColor.push(MeshColorSerializer.fromWorkerData(meshColor));
          MeshColorSerializer.disposeWorkerData(meshColor);
        }
        //console.timeEnd("meshcolor reconstruct");
        //console.time("meshcolortexture reconstruct");
        let meshColorTextures = new Array();
        for (let i = 0; i < e.data.meshColorTextures.length; ++i) {
          let meshColorTexture = e.data.meshColorTextures[i];
          let textures = {
            meshAOCurvatureColorTexture: null,
            meshPositionColorTexture: null,
            meshNormalColorTexture: null,
            meshPaintColorTexture: null
          };
          for (const [key, texture] of Object.entries(meshColorTexture)) {
            textures[key] = MeshColorTextureSerializer.fromWorkerData(
              texture,
              meshesColor[e.data.meshColorTextureRel[i]]
            );
          }
          meshColorTextures.push(textures);
        }
        //console.timeEnd("meshcolortexture reconstruct");
        // send back the result to the caller of the promise
        job.resolve(meshColorTextures);
        break;
      }
      default: {
        console.log("Compute Service: unknown command " + e.data.cmd);
      }
    }
  }
  // returns a promise
  parseSTL(text) {
    return this._postMessage({
      cmd: "parseSTL",
      text: text
    });
  }
  /**
   *
   * @param {BufferGeometry[]} modelGeometries
   * @param {number} printerColorAccuracy
   * @param {number} globalPointBudget
   * @param {number} textureToBakeNb
   * @param {WebGLRenderer} textureToBakeNb
   * @returns
   */
  computeAdjustedModelColorAccuracy(
    modelGeometries,
    printerColorAccuracy,
    globalPointBudget,
    textureToBakeNb,
    meshColorTextureMargin,
    renderer
  ) {
    const serializedGeometries = modelGeometries.map((geometry) =>
      BufferGeometrySerializer.toWorkerData(geometry)
    );
    return this._postMessage({
      cmd: "computeAdjustedModelColorAccuracy",
      modelGeometries: serializedGeometries,
      printerColorAccuracy: printerColorAccuracy,
      globalPointBudget: globalPointBudget,
      textureToBakeNb: textureToBakeNb,
      meshColorTextureMargin: meshColorTextureMargin,
      maxTextureSize: renderer.capabilities.maxTextureSize
    });
  }
  /**
   *
   * @param {BufferGeometry[]} modelGeometries
   * @param {number} adjustedAccuracy
   * @param {number} globalPointBudget
   * @returns
   */
  computeModelMeshColors(modelGeometries, adjustedAccuracy, globalPointBudget) {
    const serializedGeometries = modelGeometries.map((geometry) =>
      BufferGeometrySerializer.toWorkerData(geometry)
    );
    const transferables = new Array();
    for (let geometry of serializedGeometries) {
      let geometryData = BufferGeometrySerializer.getTransferables(geometry);
      for (let data of geometryData) transferables.push(data);
    }
    return this._postMessage(
      {
        cmd: "computeModelMeshColors",
        modelGeometries: serializedGeometries,
        adjustedAccuracy: adjustedAccuracy,
        globalPointBudget: globalPointBudget
      },
      transferables
    );
  }
  computeMeshColorsTextures(meshesColor, meshColorTextureMargin, renderer) {
    const meshesColorW = meshesColor.map((meshColor) =>
      MeshColorSerializer.toWorkerData(meshColor)
    );
    for (let meshColor of meshesColor) {
      MeshColorSerializer.disposeWorkeredMeshColor(meshColor);
    }
    const transferables = new Array();
    for (let meshColor of meshesColorW) {
      for (let transferable of MeshColorSerializer.getTransferables(meshColor))
        transferables.push(transferable);
    }
    return this._postMessage(
      {
        cmd: "computeMeshColorsTextures",
        meshesColor: meshesColorW,
        meshColorTextureMargin: meshColorTextureMargin
        //maxTextureSize: renderer.capabilities.maxTextureSize,
      },
      transferables
    );
  }
}
class WorkerService {
  #computeService = null;
  get computeService() {
    return this.#computeService;
  }
  async initService() {
    const worker = new ModuleWorker();
    await worker.load(
      "@dioxygen-software/three-js-mesh-colors/dist/three-js-mesh-colors.worker.js",
      { resolve: true }
    );
    this.#computeService = new ComputeService(worker);
  }
}
const workerService = new WorkerService();

/**
 * Store that owns the scene object as well as an root object parent of all MeshColors
 */
class SceneStore {
  scene;
  // Variables initialized in _init function
  // Contain all meshes that have to be displayed (Root node)
  meshColorRoot;
  debugRoot;
  // name of the displayed model
  meshName;
  sceneChanged;
  boneRootNodes;
  constructor(meshStore, lightStore, scene) {
    this.scene = scene ?? new Scene();
    this.meshColorRoot = new Object3D();
    this.debugRoot = new Object3D();
    this.sceneChanged = new Signal();
    this.meshName = "";
    this._init();
    this.scene.add(this.meshColorRoot);
    meshStore.meshColorAdded.on((meshColor) => {
      if (!meshColor.parent) {
        this.meshColorRoot.add(meshColor);
      }
    });
    lightStore.envMap.onChanged((envMap) => {
      this.scene.environment = envMap;
    });
    lightStore.lightAdded.on((light) => {
      this.scene.add(light);
    });
    lightStore.lightRemoved.on((light) => {
      this.scene.remove(light);
    });
    this.boneRootNodes = new Set();
  }
  _init() {
    this.meshColorRoot.clear();
    this.debugRoot.clear();
  }
  addBoneRootNode(boneRootNode) {
    if (!this.boneRootNodes.has(boneRootNode)) {
      this.boneRootNodes.add(boneRootNode);
      this.scene.add(boneRootNode);
    }
  }
  clear() {
    this._init();
  }
  // TODO scene should be change to observable but this will be changed after release
  setScene(scene) {
    this.scene = scene;
    this.sceneChanged.emit(scene);
  }
  getScene() {
    return this.scene;
  }
}

// TODO change name this is not really a store
/**
 *
 */
class TextureBakingStore {
  renderer;
  bakeMesh;
  bakeScene;
  bakeLightRoot;
  bakeCamera;
  bakeCanvas;
  bakeCanvasContext;
  materialStore;
  meshStore;
  lightStore;
  meshPaintingStore;
  constructor(
    materialStore,
    meshStore,
    renderingStore,
    lightStore,
    meshPaintingStore
  ) {
    this.renderer = renderingStore.renderer;
    this.bakeCanvas = document.createElement("canvas");
    this.bakeCanvasContext = this.bakeCanvas.getContext("2d");
    //Baking setup
    //Used to bake texture in GPU
    const quadGeometry = new BufferGeometry();
    quadGeometry.setAttribute(
      "position",
      new Float32BufferAttribute([-1, 3, 0, -1, -1, 0, 3, -1, 0], 3)
    );
    quadGeometry.setAttribute(
      "uv",
      new Float32BufferAttribute([0, 2, 0, 0, 2, 0], 2)
    );
    const quad_material = new MeshBasicMaterial();
    this.bakeMesh = new Mesh(quadGeometry, quad_material);
    this.bakeCamera = new OrthographicCamera(-1, 1, 1, -1, -1, 1);
    this.bakeScene = new Scene();
    this.bakeScene.add(this.bakeMesh);
    this.bakeLightRoot = new Object3D();
    this.bakeScene.add(this.bakeLightRoot);
    lightStore.envMap.onChanged((envMap) => {
      this.bakeScene.environment = envMap;
    });
    this.meshStore = meshStore;
    this.lightStore = lightStore;
    this.meshPaintingStore = meshPaintingStore;
    this.materialStore = materialStore;
  }
  bakeMeshLighting(uuid) {
    let bakeRenderTarget = this.materialStore.getBakeRenderTarget(uuid);
    if (!bakeRenderTarget) {
      console.warn("Mesh: " + uuid + " has no associated baking textures");
      return;
    }
    let shadingBakingMaterial =
      this.materialStore.getShadingBakingMaterial(uuid);
    if (!shadingBakingMaterial) {
      console.warn("Mesh: " + uuid + " has no associated baking material");
      return;
    }
    let meshColor = this.meshStore.getMeshColor(uuid);
    if (!meshColor) {
      console.warn("Mesh: " + uuid + " does not exist");
      return;
    }
    let meshPaintingProperties =
      this.meshPaintingStore.paintingPropertiesStore.get(uuid);
    if (!meshPaintingProperties) {
      console.warn("Mesh: " + uuid + " has no associated painting properties");
      return;
    }
    //if not brushable we still need to create a brush texture that will be used for baking
    if (!meshPaintingProperties.brushable.value) {
      this.materialStore.createPaintingTexture(uuid);
    }
    // Update bake scene lights
    this.bakeLightRoot.clear();
    for (let light of this.lightStore.getLights()) {
      this.bakeLightRoot.add(light.clone());
    }
    // Rebake painting texture with right sampler
    let oldSampler = meshColor.getColorSampler();
    const colorGradient = meshPaintingProperties.colorGradient.value;
    let optMColor = new MColor();
    let optMixedColor = new MColor();
    meshColor.setColorSampler((colorIndex, outColor) => {
      let surfaceGradient = meshColor
        .getColorSampleGeometry()
        .getAttribute("meanCurvature")
        .getX(colorIndex);
      //get color from gradient
      getColorFromTriplet(colorGradient, surfaceGradient, optMixedColor);
      //getColorFromOkLabTriplet(this.optOkLabColorGradient,surfaceGradient,this.optMixedColor);
      optMColor.setRGBA(optMixedColor.r, optMixedColor.g, optMixedColor.b, 1.0);
      let meshPtColor = meshColor.getSampleColor(colorIndex);
      //mix between already stored color and new color depending on color alpha AND
      outColor.copy(meshPtColor);
      outColor.alphaBlendColor(optMColor);
    });
    // Recompute texture
    meshPaintingProperties.colorsChanged.emit(meshColor.getFaceColors());
    //Resize renderer
    this.renderer.setSize(bakeRenderTarget.width, bakeRenderTarget.height);
    this.renderer.setRenderTarget(bakeRenderTarget);
    this.renderer.clear(true, true, false);
    this.bakeMesh.material = shadingBakingMaterial;
    this.renderer.render(this.bakeScene, this.bakeCamera);
    this.renderer.setRenderTarget(null);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    meshColor.setColorSampler(oldSampler);
    if (!meshPaintingProperties.brushable.value) {
      this.materialStore.deletePaintingTexture(uuid);
    }
    meshPaintingProperties.colorsChanged.emit(meshColor.getFaceColors());
  }
  bakeExportTexture(uuid) {
    if (this.bakeCanvasContext) {
      this.materialStore.createBakingProperties(uuid);
      this.bakeMeshLighting(uuid);
      let bakeRenderTarget = this.materialStore.getBakeRenderTarget(uuid);
      if (!bakeRenderTarget) {
        return null;
      }
      let width = bakeRenderTarget.width;
      let height = bakeRenderTarget.height;
      let buffer = new Uint8Array(width * height * 4);
      this.renderer.readRenderTargetPixels(
        bakeRenderTarget,
        0,
        0,
        width,
        height,
        buffer
      );
      // Result of readRenderTargetPixel is flipped vertically
      // we flip it for baking
      let halfHeight = (height / 2) | 0; // | 0 fastest way to convert float to an int
      let bytesPerRow = width * 4;
      // make a temp buffer to hold one row
      let tempRow = new Uint8Array(width * 4);
      for (let y = 0; y < halfHeight; ++y) {
        let bottomOffset = y * bytesPerRow;
        let topOffset = (width - y - 1) * bytesPerRow;
        // make copy of a row on the bottom half
        tempRow.set(buffer.subarray(bottomOffset, bottomOffset + bytesPerRow));
        // copy a row from the top half to the bottom
        buffer.copyWithin(bottomOffset, topOffset, topOffset + bytesPerRow);
        // copy the copy of the bottom row to the top half row
        buffer.set(tempRow, topOffset);
      }
      this.bakeCanvas.width = width;
      this.bakeCanvas.height = height;
      let imageData = this.bakeCanvasContext.createImageData(width, height);
      for (let i = 0; i < imageData.data.length; i++) {
        imageData.data[i] = buffer[i];
      }
      this.bakeCanvasContext.putImageData(imageData, 0, 0);
      this.materialStore.deleteBakingProperties(uuid);
      return this.bakeCanvas.toDataURL("image/png");
    }
    return null;
  }
}

// ID shader SETUP
const idMaterial = new ShaderMaterial({
  vertexShader: /* glsl */ `

       #include <skinning_pars_vertex>

        void main(){
         #include <skinbase_vertex>
         #include <begin_vertex>
         #include <skinning_vertex>
            #include <project_vertex>
            gl_Position = projectionMatrix * mvPosition ;
        }`,
  fragmentShader: /* glsl */ `
        uniform int id;
        float rand(float co) {return (sin(co*12.98))*0.5 + 1.0;}
        void main(){
            vec2 randIdVecR = vec2(id,id + 1);
            vec2 randIdVecG = vec2(id + 1,id + 2);
            vec2 randIdVecB = vec2(id + 2,id + 3);
            gl_FragColor = vec4(rand(float(id) + 0.1), rand(float(id)+0.2), rand(float(id)+0.3),1.0);
        }`,
  uniforms: { id: { value: 0 } } //UniformsUtils.merge([UniformsLib.common,{id: {value : 0}}])
});
// Silhouette shader SETUP
const silhouetteMaterial = new ShaderMaterial({
  vertexShader: /* glsl */ `
        out vec2 vUv;
        void main(){
        	vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);
        }`,
  fragmentShader: /* glsl */ `
        uniform vec3 silhouetteColor;
        uniform sampler2D renderTex;
		uniform sampler2D idTex;
		uniform vec2 texture_size;

        in vec2 vUv;

        float silhouetteStrength()
        {
            vec2 size = texture_size;
			float resx = 1.0 / size.x;
			float resy = 1.0 / size.y;

			float x = vUv.x;
			float y = vUv.y;

			// Sample input texture
			float h00 = texture(idTex, vec2(x - resx, y - resy)).x;
			float h01 = texture(idTex, vec2(x       , y - resy)).x;
			float h02 = texture(idTex, vec2(x + resx, y - resy)).x;
			float h10 = texture(idTex, vec2(x - resx, y)).x;
			float h11 = texture(idTex, vec2(x       , y)).x;
			float h12 = texture(idTex, vec2(x + resx, y)).x;
			float h20 = texture(idTex, vec2(x - resx, y + resy)).x;
			float h21 = texture(idTex, vec2(x       , y + resy)).x;
			float h22 = texture(idTex, vec2(x + resx, y + resy)).x;

			// Compute gradient and test if we are on an edge
			float Gx = -h00 + h02 - 2.0 * h10 + 2.0 * h12 - h20 + h22;
			float Gy = -h00 - 2.0 * h01 - h02 + h20 + 2.0 * h21 + h22;

			float Gn = Gx * Gx + Gy * Gy;
			return smoothstep(0.0, 1.0, Gn);
        }

        void main()
        {
            float silhouette = silhouetteStrength();
            vec4 renderColor = texture(renderTex, vUv);
            gl_FragColor = mix(renderColor, vec4(silhouetteColor,1.0), max(0.0,silhouette - 0.5*texture(idTex,vUv).a));
        }`,
  uniforms: {
    silhouetteColor: { value: new Color(1, 0.5, 0.1) },
    renderTex: { value: null },
    idTex: { value: null },
    texture_size: { value: new Vector2() }
  }
});

const RenderingPipelineType = {
  DIRECT: "DIRECT",
  NO_OUTILNE: "NO_OUTLINE",
  IDS: "IDS",
  DENSITY: "DENSITY"
};
/**
 * Store that owns the renderer, the effect composer and th rendering pippeline
 */
class RenderingStore {
  // CORE DATA ------------------------
  renderingId;
  antiAliasing;
  renderingPipelineType;
  // COMPUTED ----------------------------
  // computed for now but we could store renderer parameters ?
  renderer;
  sceneStore;
  cameraStore;
  materialStore;
  composer;
  renderPass;
  densityPass;
  idPass;
  silhouettePass;
  constructor(renderer, sceneStore, cameraStore, materialStore) {
    this.renderer = renderer ?? new WebGLRenderer();
    this.sceneStore = sceneStore;
    this.cameraStore = cameraStore;
    this.materialStore = materialStore;
    this.renderingId = new Observable(0);
    this.antiAliasing = new Observable(false);
    this.antiAliasing.onChanged((aa) => {
      this.renderPass.setSampleLevelMin(0);
      this.renderPass.setSampleLevelMax(aa ? 4 : 0);
    });
    this.renderingPipelineType = new Observable(RenderingPipelineType.DIRECT);
    this.renderingPipelineType.onChanged(() => {
      this.setUpPipeline();
    });
    this.sceneStore.sceneChanged.on((scene) => {
      this.setScene(scene);
    });
    this.composer = new EffectComposer(renderer);
    let rendererSize = new Vector2();
    this.renderer.getSize(rendererSize);
    this.composer.setPixelRatio(renderer.getPixelRatio());
    this.composer.setSize(rendererSize.width, rendererSize.height);
    // PASSES SETUP
    this.createRenderingPipeline();
  }
  createRenderingPipeline() {
    let renderOptions = {
      scene: this.sceneStore.getScene(),
      camera: this.cameraStore.camera.value,
      renderer: this.renderer,
      ssaaLevelMin: 0,
      ssaaLevelMax: this.antiAliasing.value ? 4 : 0
    };
    this.renderPass = new RenderPass({
      ...renderOptions,
      dependTextures: {},
      targets: ["rendered_scene"]
    });
    this.densityPass = new RenderPass({
      ...renderOptions,
      dependTextures: {},
      targets: ["rendered_scene"],
      overrideMaterial: "density",
      materialSwitcher: this.materialStore.materialSwitcher
    });
    this.idPass = new RenderPass({
      ...renderOptions,
      dependTextures: {},
      targets: ["id"],
      overrideMaterial: "id",
      materialSwitcher: this.materialStore.materialSwitcher,
      clearColor: new Color(1, 1, 1),
      clearAlpha: 0.0
    });
    this.silhouettePass = new ShaderPass({
      shaderMat: silhouetteMaterial,
      renderer: this.renderer,
      dependTextures: { renderTex: "rendered_scene", idTex: "id" },
      targets: ["Silhouette"]
    });
    this.setUpPipeline();
  }
  setScene(scene) {
    if (this.renderPass) this.renderPass.setScene(scene);
    if (this.densityPass) this.densityPass.setScene(scene);
    if (this.idPass) this.idPass.setScene(scene);
  }
  setUpPipeline() {
    this.composer.clear();
    if (this.renderingPipelineType.value === RenderingPipelineType.IDS) {
      this.composer.addPass(this.idPass);
    } else if (
      this.renderingPipelineType.value === RenderingPipelineType.DENSITY
    ) {
      this.composer.addPass(this.densityPass);
    } else {
      this.composer.addPass(this.renderPass);
      if (this.renderingPipelineType.value === RenderingPipelineType.DIRECT) {
        this.composer.addPass(this.idPass);
        this.composer.addPass(this.silhouettePass);
      }
    }
  }
  render() {
    this.composer.render();
  }
  needsRender() {
    if (this.renderPass) this.renderPass.hasChanged();
    if (this.densityPass) this.densityPass.hasChanged();
    if (this.idPass) this.idPass.hasChanged();
  }
  setSize(width, height) {
    this.renderer.setSize(width, height);
    this.composer.setSize(width, height);
  }
}

/**
 * Store that contains color samples values, painting properties (like the gradient)
 * and shading properties
 */
class MeshPaintingProperties {
  // CORE DATA ---------------
  aoIntensity;
  aoWhiteThreshold;
  roughness;
  metalness;
  envMapIntensity;
  colorGradient;
  brushable;
  // Optimization Variables
  optMColor = new MColor();
  optMixedColor = new MColor();
  optVec3 = new Vector3();
  optOkLabColorGradient = {
    lowColor: new Vector3(1, 1, 1),
    lowColorAlpha: 1.0,
    lowColorLevel: 0.0,
    mediumColor: new Vector3(1, 1, 1),
    mediumColorAlpha: 1.0,
    mediumColorLevel: 0.5,
    highColor: new Vector3(1, 1, 1),
    highColorAlpha: 1.0,
    highColorLevel: 1.0
  };
  // Computed ---------------
  //
  meshPoints = 0;
  meshSurface = 0;
  meshVolume = 0;
  // Pointer to the Mesh that contains
  mesh;
  // SIGNALS ----------------------
  colorsChanged;
  constructor(mesh) {
    //Rendering pros----------
    this.aoIntensity = new Observable(1.0);
    this.aoWhiteThreshold = new Observable(1.0);
    this.roughness = new Observable(0.5);
    this.metalness = new Observable(0.5);
    this.envMapIntensity = new Observable(2.7);
    this.brushable = new Observable(true);
    this.colorGradient = new Observable({
      lowColor: new Color(1, 1, 1),
      lowColorAlpha: 1.0,
      lowColorLevel: 0.0,
      mediumColor: new Color(1, 1, 1),
      mediumColorAlpha: 1.0,
      mediumColorLevel: 0.5,
      highColor: new Color(1, 1, 1),
      highColorAlpha: 1.0,
      highColorLevel: 1.0
    });
    this.colorGradient.onChanged((colorGradient) => {
      this.optVec3.set(
        colorGradient.lowColor.r,
        colorGradient.lowColor.g,
        colorGradient.lowColor.b
      );
      RGBToOklab(this.optVec3, this.optOkLabColorGradient.lowColor);
      this.optVec3.set(
        colorGradient.mediumColor.r,
        colorGradient.mediumColor.g,
        colorGradient.mediumColor.b
      );
      RGBToOklab(this.optVec3, this.optOkLabColorGradient.mediumColor);
      this.optVec3.set(
        colorGradient.highColor.r,
        colorGradient.highColor.g,
        colorGradient.highColor.b
      );
      RGBToOklab(this.optVec3, this.optOkLabColorGradient.highColor);
      this.optOkLabColorGradient.lowColorAlpha = colorGradient.lowColorAlpha;
      this.optOkLabColorGradient.lowColorLevel = colorGradient.lowColorLevel;
      this.optOkLabColorGradient.mediumColorAlpha =
        colorGradient.mediumColorAlpha;
      this.optOkLabColorGradient.mediumColorLevel =
        colorGradient.mediumColorLevel;
      this.optOkLabColorGradient.highColorAlpha = colorGradient.highColorAlpha;
      this.optOkLabColorGradient.highColorLevel = colorGradient.highColorLevel;
      //----------------------------------------------------
    });
    this.mesh = mesh;
    this.setBrushableColorSampler();
    this.brushable.onChanged((brushable) => {
      this.setBrushableColorSampler();
    });
    this.colorsChanged = new Signal();
  }
  setBrushableColorSampler() {
    if (this.brushable.value) {
      this.mesh.setColorSampler((colorIndex, outColor) => {
        let surfaceGradient = this.mesh
          .getColorSampleGeometry()
          .getAttribute("meanCurvature")
          .getX(colorIndex);
        //get color from gradient
        getColorFromTriplet(
          this.colorGradient.value,
          surfaceGradient,
          this.optMixedColor
        );
        //getColorFromOkLabTriplet(this.optOkLabColorGradient,surfaceGradient,this.optMixedColor);
        this.optMColor.setRGBA(
          this.optMixedColor.r,
          this.optMixedColor.g,
          this.optMixedColor.b,
          1.0
        );
        let meshPtColor = this.mesh.getSampleColor(colorIndex);
        //mix between already stored color and new color depending on color alpha AND
        outColor.copy(meshPtColor);
        outColor.alphaBlendColor(this.optMColor);
      });
    } else {
      this.mesh.setColorSampler((colorIndex, outColor) => {
        outColor.copy(this.mesh.getSampleColor(colorIndex));
      });
    }
  }
  saveColorsToBufferAttribute(attributeName) {
    this.mesh.saveColorsToAttribute(attributeName);
  }
  setBufferAttributeAsColor(attributeName) {
    this.mesh.setColorsFromGeometryAttribute(attributeName, false);
    this.colorsChanged.emit(this.mesh.getFaceColors());
  }
  erase(vertexSelection) {
    let points = normalizeVertexSelection(vertexSelection);
    for (let pt of points) {
      let meshPtColor = this.mesh.getSampleColor(pt.index);
      meshPtColor.a = meshPtColor.a * (1.0 - pt.alpha);
    }
    this.colorsChanged.emit(
      getMeshColorFacesFromVertexSelection(this.mesh, points)
    );
  }
  applyColorGradient(vertexSelection, colorGradientTriplet) {
    let points = normalizeVertexSelection(vertexSelection);
    meshColorSurfaceGradientColoring(
      this.mesh,
      points,
      "meanCurvature", // TODO put as an argument ?
      colorGradientTriplet
    );
    this.colorsChanged.emit(
      getMeshColorFacesFromVertexSelection(this.mesh, points)
    );
  }
  resetPaintedData() {
    this.mesh.resetColorSamples();
    this.colorsChanged.emit(this.mesh.faceColors);
  }
  toJSON() {
    const colorGradient = this.colorGradient.value;
    return JSON.stringify({
      aoIntensity: this.aoIntensity.value,
      aoWhiteThreshold: this.aoWhiteThreshold.value,
      roughness: this.roughness.value,
      metalness: this.metalness.value,
      envMapIntensity: this.envMapIntensity.value,
      colorGradient: {
        lowColor: {
          r: colorGradient.lowColor.r,
          g: colorGradient.lowColor.g,
          b: colorGradient.lowColor.b
        },
        lowColorAlpha: colorGradient.lowColorAlpha,
        lowColorLevel: colorGradient.lowColorLevel,
        mediumColor: {
          r: colorGradient.mediumColor.r,
          g: colorGradient.mediumColor.g,
          b: colorGradient.mediumColor.b
        },
        mediumColorAlpha: colorGradient.mediumColorAlpha,
        mediumColorLevel: colorGradient.mediumColorLevel,
        highColor: {
          r: colorGradient.highColor.r,
          g: colorGradient.highColor.g,
          b: colorGradient.highColor.b
        },
        highColorAlpha: colorGradient.highColorAlpha,
        highColorLevel: colorGradient.highColorLevel
      },
      brushable: this.brushable.value
    });
  }
  fromJSON(json) {
    let jsonObj = JSON.parse(json);
    if (jsonObj.aoIntensity !== undefined)
      this.aoIntensity.value = jsonObj.aoIntensity;
    if (jsonObj.aoWhiteThreshold !== undefined)
      this.aoWhiteThreshold.value = jsonObj.aoWhiteThreshold;
    if (jsonObj.roughness !== undefined)
      this.roughness.value = jsonObj.roughness;
    if (jsonObj.metalness !== undefined)
      this.metalness.value = jsonObj.metalness;
    if (jsonObj.envMapIntensity !== undefined)
      this.envMapIntensity.value = jsonObj.envMapIntensity;
    if (jsonObj.colorGradient !== undefined) {
      this.colorGradient.value = {
        lowColor: new Color(
          jsonObj.colorGradient.lowColor.r,
          jsonObj.colorGradient.lowColor.g,
          jsonObj.colorGradient.lowColor.b
        ),
        lowColorAlpha: jsonObj.colorGradient.lowColorAlpha,
        lowColorLevel: jsonObj.colorGradient.lowColorLevel,
        mediumColor: new Color(
          jsonObj.colorGradient.mediumColor.r,
          jsonObj.colorGradient.mediumColor.g,
          jsonObj.colorGradient.mediumColor.b
        ),
        mediumColorAlpha: jsonObj.colorGradient.mediumColorAlpha,
        mediumColorLevel: jsonObj.colorGradient.mediumColorLevel,
        highColor: new Color(
          jsonObj.colorGradient.highColor.r,
          jsonObj.colorGradient.highColor.g,
          jsonObj.colorGradient.highColor.b
        ),
        highColorAlpha: jsonObj.colorGradient.highColorAlpha,
        highColorLevel: jsonObj.colorGradient.highColorLevel
      };
    }
    if (jsonObj.brushable !== undefined)
      this.brushable.value = jsonObj.brushable;
  }
  loadColorData(colorData, colorMetaData = undefined, onLoaded = () => {}) {
    MeshColorSerializer.loadColorData(
      this.mesh,
      colorData,
      colorMetaData,
      onLoaded
    );
    this.colorsChanged.emit(this.mesh.getFaceColors());
  }
}
/**
 *
 */
class MeshPaintingStore {
  // CORE DATA -------------------------
  paintingPropertiesStore;
  //TEMPORARY FOR CURRENT VERSION REMOVE AND PUT BACK SAMPLER AFTERWARDS
  selectionStore;
  // SIGNAL ---------------------------
  propertyAdded;
  propertyRemoved;
  constructor(meshStore, selectionStore) {
    this.selectionStore = selectionStore;
    this.paintingPropertiesStore = new Map();
    this.propertyAdded = new Signal();
    this.propertyRemoved = new Signal();
    meshStore.meshColorAdded.on((meshColor) => {
      this.addPaintingProperty(meshColor.name, meshColor);
    });
    meshStore.meshColorRemoved.on((meshUUID) => {
      this.removePaintingProperty(meshUUID);
    });
  }
  addPaintingProperty(meshUUID, meshColor) {
    if (this.paintingPropertiesStore.has(meshUUID)) {
      console.warn("Mesh " + meshUUID + " already in store");
      return;
    }
    let meshPaintingProperties = new MeshPaintingProperties(
      meshColor,
      this.selectionStore
    );
    this.paintingPropertiesStore.set(meshUUID, meshPaintingProperties);
    this.propertyAdded.emit({
      uuid: meshUUID,
      meshPaintingProperties: meshPaintingProperties
    });
  }
  removePaintingProperty(meshUUID) {
    if (this.paintingPropertiesStore.has(meshUUID)) {
      this.paintingPropertiesStore.delete(meshUUID);
      this.propertyRemoved.emit(meshUUID);
    }
  }
  clear() {
    this.paintingPropertiesStore.clear();
  }
}

/**
 * UUID.js - RFC-compliant UUID Generator for JavaScript
 *
 * @author  LiosK
 * @version v5.1.0
 * @license Apache License 2.0: Copyright (c) 2010-2024 LiosK
 * @packageDocumentation
 */
var _a;
/**
 * The UUID object type.
 */
class UUID {
  // Core Component {{{
  /**
   * Generates a UUIDv4 as a hexadecimal string.
   * @returns The hexadecimal UUID string.
   */
  static generate() {
    var rand = _a._getRandomInt,
      hex = _a._hexAligner;
    return (
      hex(rand(32), 8) + // time_low
      "-" +
      hex(rand(16), 4) + // time_mid
      "-" +
      hex(0x4000 | rand(12), 4) + // time_hi_and_version
      "-" +
      hex(0x8000 | rand(14), 4) + // clock_seq_hi_and_reserved clock_seq_low
      "-" +
      hex(rand(48), 12) // node
    );
  }
  /**
   * Returns an unsigned `x`-bit random integer.
   * @param x - An unsigned integer ranging from 0 to 53, inclusive.
   * @returns An unsigned `x`-bit random integer (`0 <= f(x) < 2^x`).
   */
  static _getRandomInt(x) {
    if (x < 0 || x > 53) {
      return NaN;
    }
    var n = 0 | (Math.random() * 0x40000000); // 1 << 30
    return x > 30
      ? n + (0 | (Math.random() * (1 << (x - 30)))) * 0x40000000
      : n >>> (30 - x);
  }
  /**
   * Converts an integer to a zero-filled hexadecimal string.
   */
  static _hexAligner(num, length) {
    var str = num.toString(16),
      i = length - str.length,
      z = "0";
    for (; i > 0; i >>>= 1, z += z) {
      if (i & 1) {
        str = z + str;
      }
    }
    return str;
  }
  /**
   * Enables Math.random()-based pseudorandom number generator instead of cryptographically safer options.
   * @since v3.5.0
   * @deprecated This method is provided only to work around performance drawbacks of the safer algorithms.
   */
  static useMathRandom() {
    _a._getRandomInt = _a._mathPRNG;
  }
  /**
   * Creates a UUIDv4 object.
   * @returns A UUIDv4 object.
   * @since 3.0
   */
  static genV4() {
    var rand = _a._getRandomInt;
    return new _a(
      rand(32), // time_low
      rand(16), // time_mid
      0x4000 | rand(12), // time_hi_and_version
      0x80 | rand(6), // clock_seq_hi_and_reserved
      rand(8), // clock_seq_low
      rand(48)
    );
  }
  /**
   * Converts a hexadecimal UUID string to a UUID object.
   * @param strId - A hexadecimal UUID string ("xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx").
   * @returns The UUID object or `null`.
   * @since 3.0
   */
  static parse(strId) {
    var r,
      p =
        /^\s*(urn:uuid:|\{)?([0-9a-f]{8})-([0-9a-f]{4})-([0-9a-f]{4})-([0-9a-f]{2})([0-9a-f]{2})-([0-9a-f]{12})(\})?\s*$/i;
    if ((r = p.exec(strId))) {
      var l = r[1] || "",
        t = r[8] || "";
      if (
        l + t === "" ||
        (l === "{" && t === "}") ||
        (l.toLowerCase() === "urn:uuid:" && t === "")
      ) {
        return new _a(
          parseInt(r[2], 16),
          parseInt(r[3], 16),
          parseInt(r[4], 16),
          parseInt(r[5], 16),
          parseInt(r[6], 16),
          parseInt(r[7], 16)
        );
      }
    }
    return null;
  }
  /**
   * Initializes the UUID object.
   * @param _timeLow - The time_low field (octet 0-3, uint32).
   * @param _timeMid - The time_mid field (octet 4-5, uint16).
   * @param _timeHiAndVersion - The time_hi_and_version field (octet 6-7, uint16).
   * @param _clockSeqHiAndReserved - The clock_seq_hi_and_reserved field (octet 8, uint8).
   * @param _clockSeqLow - The clock_seq_low field (octet 9, uint8).
   * @param _node - The node field (octet 10-15, uint48).
   */
  constructor(
    _timeLow,
    _timeMid,
    _timeHiAndVersion,
    _clockSeqHiAndReserved,
    _clockSeqLow,
    _node
  ) {
    var names = _a.FIELD_NAMES,
      sizes = _a.FIELD_SIZES;
    var bin = _a._binAligner,
      hex = _a._hexAligner;
    // @ts-ignore
    this.intFields = new Array(6);
    // @ts-ignore
    this.bitFields = new Array(6);
    // @ts-ignore
    this.hexFields = new Array(6);
    for (var i = 0; i < 6; i++) {
      var intValue = parseInt(arguments[i] || 0);
      // @ts-ignore
      this.intFields[i] = this.intFields[names[i]] = intValue;
      // @ts-ignore
      this.bitFields[i] = this.bitFields[names[i]] = bin(intValue, sizes[i]);
      // @ts-ignore
      this.hexFields[i] = this.hexFields[names[i]] = hex(
        intValue,
        sizes[i] >>> 2
      );
    }
    this.version = (this.intFields.timeHiAndVersion >>> 12) & 0xf;
    this.bitString = this.bitFields.join("");
    this.hexNoDelim = this.hexFields.join("");
    this.hexString =
      this.hexFields[0] +
      "-" +
      this.hexFields[1] +
      "-" +
      this.hexFields[2] +
      "-" +
      this.hexFields[3] +
      this.hexFields[4] +
      "-" +
      this.hexFields[5];
    this.urn = "urn:uuid:" + this.hexString;
  }
  /**
   * Converts an integer to a zero-filled binary string.
   */
  static _binAligner(num, length) {
    var str = num.toString(2),
      i = length - str.length,
      z = "0";
    for (; i > 0; i >>>= 1, z += z) {
      if (i & 1) {
        str = z + str;
      }
    }
    return str;
  }
  /**
   * Returns the hexadecimal string representation.
   * @returns {@link UUID#hexString}.
   */
  toString() {
    return this.hexString;
  }
  /**
   * Tests if two UUID objects are equal.
   * @returns `true` if two UUID objects are equal.
   */
  equals(uuid) {
    if (!(uuid instanceof _a)) {
      return false;
    }
    for (var i = 0; i < 6; i++) {
      if (this.intFields[i] !== uuid.intFields[i]) {
        return false;
      }
    }
    return true;
  }
  // }}}
  // UUIDv1 Component (1 of 2) {{{
  /**
   * Creates a UUIDv1 object.
   * @returns A UUIDv1 object.
   * @since 3.0
   */
  static genV1() {
    if (_a._state == null) {
      _a._state = new UUIDState();
    }
    var now = new Date().getTime(),
      st = _a._state;
    if (now != st.timestamp) {
      if (now < st.timestamp) {
        st.sequence++;
      }
      st.timestamp = now;
      st.tick = _a._getRandomInt(12); // up to 4095, allowing 5904 tick per msec
    } else if (st.tick < 9992) {
      // advance sub-millisecond fraction up to 9999 100-nanoseconds
      st.tick += 1 + _a._getRandomInt(3);
    } else {
      // advance seq if tick overflows in remote chance
      st.sequence++;
    }
    // format time fields
    var tf = _a._getTimeFieldValues(st.timestamp);
    var tl = tf.low + st.tick;
    var thav = (tf.hi & 0xfff) | 0x1000; // set version '0001'
    // format clock sequence
    st.sequence &= 0x3fff;
    var cshar = (st.sequence >>> 8) | 0x80; // set variant '10'
    var csl = st.sequence & 0xff;
    return new _a(tl, tf.mid, thav, cshar, csl, st.node);
  }
  /**
   * Re-initializes the internal state for UUIDv1 and UUIDv6 creation.
   * @since 3.0
   */
  static resetState() {
    _a._state = new UUIDState();
  }
  /**
   * @param time - The number of milliseconds elapsed since 1970-01-01.
   */
  static _getTimeFieldValues(time) {
    var ts = time - Date.UTC(1582, 9, 15);
    var hm = ((ts / 0x100000000) * 10000) & 0xfffffff;
    return {
      low: ((ts & 0xfffffff) * 10000) % 0x100000000,
      mid: hm & 0xffff,
      hi: hm >>> 16,
      timestamp: ts
    };
  }
  // }}}
  // UUIDv6 Component {{{
  /**
   * Creates a UUIDv6 object.
   * @returns A UUIDv6 object.
   * @since v4.2.13
   */
  static genV6() {
    if (_a._state == null) {
      _a._state = new UUIDState();
    }
    var now = new Date().getTime(),
      st = _a._state;
    if (now != st.timestamp) {
      if (now < st.timestamp) {
        st.sequence++;
      }
      st.timestamp = now;
      st.tick = _a._getRandomInt(12); // up to 4095, allowing 5904 tick per msec
    } else if (st.tick < 9992) {
      // advance sub-millisecond fraction up to 9999 100-nanoseconds
      st.tick += 1 + _a._getRandomInt(3);
    } else {
      // advance seq if tick overflows in remote chance
      st.sequence++;
    }
    // format time fields
    var ts = st.timestamp - Date.UTC(1582, 9, 15);
    var th = Math.floor((ts / 0x10000000) * 10000) % 0x100000000;
    var midlow = (((ts & 0xfffffff) * 10000) & 0xfffffff) + st.tick;
    var tm = midlow >>> 12;
    var tlav = (midlow & 0xfff) | 0x6000; // set version '0110'
    // format clock sequence
    st.sequence &= 0x3fff;
    var cshar = (st.sequence >>> 8) | 0x80; // set variant '10'
    var csl = st.sequence & 0xff;
    return new _a(th, tm, tlav, cshar, csl, st.node);
  }
}
_a = UUID;
// }}}
// Advanced Random Number Generator Component {{{
UUID._mathPRNG = _a._getRandomInt;
(() => {
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    // Web Cryptography API
    _a._getRandomInt = (x) => {
      if (x < 0 || x > 53) {
        return NaN;
      }
      var ns = new Uint32Array(x > 32 ? 2 : 1);
      crypto.getRandomValues(ns);
      return x > 32
        ? ns[0] + (ns[1] >>> (64 - x)) * 0x100000000
        : ns[0] >>> (32 - x);
    };
  }
})();
// }}}
// UUID Object Component {{{
/**
 * The names of UUID internal fields.
 *
 * Note that these internal fields from the obsolete RFC 4122 are no longer
 * used in the current RFC 9562.
 * @since 3.0
 */
UUID.FIELD_NAMES = [
  "timeLow",
  "timeMid",
  "timeHiAndVersion",
  "clockSeqHiAndReserved",
  "clockSeqLow",
  "node"
];
/**
 * The sizes of UUID internal fields.
 *
 * Note that these internal fields from the obsolete RFC 4122 are no longer
 * used in the current RFC 9562.
 * @since 3.0
 */
UUID.FIELD_SIZES = [32, 16, 16, 8, 8, 48];
/**
 * A nil UUID object.
 * @since v3.4.0
 */
UUID.NIL = new _a(0, 0, 0, 0, 0, 0);
/**
 * The persistent internal state for UUIDv1 and UUIDv6 creation.
 */
UUID._state = null;
// UUIDv1 Component (2 of 2) {{{
class UUIDState {
  constructor() {
    // @ts-ignore
    var rand = UUID._getRandomInt;
    this.timestamp = 0;
    this.tick = 0; // timestamp fraction smaller than a millisecond
    this.sequence = rand(14);
    this.node = (rand(8) | 1) * 0x10000000000 + rand(40); // set multicast bit '1'
  }
}
// }}}
// vim: fdm=marker fmr&

class ImportBackend {
  static nextGroupId = UUID.generate();
  // TODO move in worker asap
  static processImportGeometry(geometry) {
    this.nextGroupId = UUID.generate();
    geometry.userData["MeshColorGroupGeometry"] = this.nextGroupId;
    geometry.deleteAttribute("normal");
    let indexedGeometry = BufferGeometryUtils.mergeVertices(geometry, 1e-7);
    indexedGeometry.computeVertexNormals();
    indexedGeometry.userData["MeshColorGroupGeometry"] = this.nextGroupId;
    // Edge case remove indexed degenerated triangles
    // (triangle that contains at least two identical indices)
    if (indexedGeometry.index) {
      let indicesToRemove = new Set();
      for (let i = 0; i < indexedGeometry.index.count; i += 3) {
        let index0 = indexedGeometry.index.getX(i);
        let index1 = indexedGeometry.index.getX(i + 1);
        let index2 = indexedGeometry.index.getX(i + 2);
        if (index0 == index1 || index0 == index2 || index1 == index2) {
          indicesToRemove.add(i);
          indicesToRemove.add(i + 1);
          indicesToRemove.add(i + 2);
        }
      }
      if (indicesToRemove.size > 0) {
        console.warn(
          "[MeshColor]: Imported geometry " +
            geometry.name +
            " has degenerated triangles"
        );
        const indexArray = indexedGeometry.index.array;
        let filteredIndexArray = [];
        for (let i = 0; i < indexArray.length; ++i) {
          if (!indicesToRemove.has(i)) {
            filteredIndexArray.push(indexArray[i]);
          }
        }
        indexedGeometry.setIndex(filteredIndexArray);
      }
    }
    let splitGeometries =
      BufferGeometryComputations.splitIntoConnectedParts(indexedGeometry);
    let filteredGeometries = [];
    // We filter geometries that have loose points or no triangles
    for (const geom of splitGeometries) {
      const positionAttr = geom.getAttribute("position");
      if (
        positionAttr &&
        positionAttr.count >= 3 &&
        geom.index &&
        geom.index.count >= 3
      ) {
        filteredGeometries.push(geom);
      } else {
        console.warn(
          "[MeshColor]: Imported geometry " +
            geometry.name +
            " has some loose faces/edges/points"
        );
      }
    }
    splitGeometries = filteredGeometries;
    for (let i = 0; i < splitGeometries.length; ++i) {
      if (geometry.name) {
        if (splitGeometries.length > 1)
          splitGeometries[i].name = geometry.name + "-" + i.toString();
        else splitGeometries[i].name = geometry.name;
      }
      splitGeometries[i].userData["MeshColorGroupGeometry"] = this.nextGroupId;
    }
    return splitGeometries;
  }
  /**
   * Get meshes and Skinned Meshes out a root Object
   * This function creates clone of meshes and skinned meshes
   * @param rootObj
   * @returns
   */
  static processImportObjects(rootObj, clonnedMeshPreProcess) {
    const meshes = [];
    const skinnedMeshes = [];
    const otherObjects = [];
    // associate a mesh from rootObj to its cloned mesh
    const clonnedMeshMap = new Map();
    rootObj.traverse((o) => {
      if (o instanceof Mesh && o.visible) {
        let mesh = o.clone();
        mesh.geometry = mesh.geometry.clone();
        mesh.userData = o.userData;
        if (clonnedMeshPreProcess) {
          clonnedMeshPreProcess(mesh);
        }
        clonnedMeshMap.set(o, mesh);
        if (!mesh.geometry.name || mesh.geometry.name == "")
          mesh.geometry.name = mesh.name;
        // TODO remove all non position and normal attributes ?
        mesh.geometry.deleteAttribute("color");
        mesh.geometry.deleteAttribute("uv");
        mesh.geometry.computeVertexNormals();
        // parent assignation
        // the traverse function is top down so we only need to test parents
        let clonnedParent = o.parent ? clonnedMeshMap.get(o.parent) : null;
        if (clonnedParent) {
          mesh.parent = clonnedParent;
        }
        // case where parent is not a mesh
        // the parent is not cloned
        else if (o.parent) {
          mesh.parent = o.parent;
        }
        if (mesh instanceof SkinnedMesh && o instanceof SkinnedMesh) {
          mesh.bindMode = o.bindMode;
          mesh.bind(o.skeleton, o.bindMatrix);
          skinnedMeshes.push(mesh);
        } else {
          meshes.push(mesh);
        }
      } else {
        //No clone in this case.
        otherObjects.push(o);
      }
    });
    // associate a clonned mesh to its geometry processed version
    // if a mesh is split into several ones, the map value points
    // toward the last element of the split meshes
    const clonnedSplitMeshMap = new Map();
    const splitMeshes = [];
    for (const mesh of meshes) {
      let geometries = ImportBackend.processImportGeometry(mesh.geometry);
      for (let i = 0; i < geometries.length; ++i) {
        const newMesh = mesh.clone();
        clonnedSplitMeshMap.set(mesh, newMesh);
        newMesh.geometry = geometries[i];
        // Restore parent if any
        // meshes are stored in a top down fashion, we only need to store the parents
        if (mesh.parent instanceof Mesh) {
          newMesh.parent = clonnedSplitMeshMap.get(mesh.parent) ?? null;
        } else {
          newMesh.parent = mesh.parent;
        }
        // Assign name
        if (mesh.name) {
          if (geometries.length > 1)
            newMesh.name = mesh.name + "-" + i.toString();
          else newMesh.name = mesh.name;
        }
        splitMeshes.push(newMesh);
      }
    }
    const splitSkinnedMeshes = [];
    for (const skinnedMesh of skinnedMeshes) {
      let geometries = ImportBackend.processImportGeometry(
        skinnedMesh.geometry
      );
      for (let i = 0; i < geometries.length; ++i) {
        const newSkinnedMesh = skinnedMesh.clone();
        newSkinnedMesh.geometry = geometries[i];
        newSkinnedMesh.bindMode = skinnedMesh.bindMode;
        newSkinnedMesh.bind(skinnedMesh.skeleton, skinnedMesh.bindMatrix);
        clonnedSplitMeshMap.set(skinnedMesh, newSkinnedMesh);
        // meshes are stored in a top down fashion, we only need to store the parents
        if (skinnedMesh.parent instanceof Mesh) {
          newSkinnedMesh.parent =
            clonnedSplitMeshMap.get(skinnedMesh.parent) ?? null;
        } else {
          newSkinnedMesh.parent = skinnedMesh.parent;
        }
        // Assign name
        if (skinnedMesh.name) {
          if (geometries.length > 1)
            newSkinnedMesh.name = skinnedMesh.name + "-" + i.toString();
          else newSkinnedMesh.name = skinnedMesh.name;
        }
        splitSkinnedMeshes.push(newSkinnedMesh);
      }
    }
    return {
      meshes: splitMeshes,
      skinnedMeshes: splitSkinnedMeshes,
      otherObjects: otherObjects
    };
  }
  /**
   * Get Meshes, Skinned Meshes and Bone roots
   * out of a loaded FBX model ouput
   * @param rootObj
   * @returns
   */
  static processFBXModel(rootObj) {
    const rootBoneNodes = [];
    const meshes = ImportBackend.processImportObjects(rootObj);
    for (let child of rootObj.children) {
      if (child.children.length == 1 && child.children[0] instanceof Bone) {
        rootBoneNodes.push(child);
      }
    }
    return {
      ...meshes,
      rootBoneNodes: rootBoneNodes
    };
  }
  // TODO move in worker when Mesh serialization has been done
  static async loadFBX(fileContent) {
    return new Promise((resolve, reject) => {
      const obj = new FBXLoader().parse(fileContent, "");
      resolve(obj);
    });
  }
}

const FileType = {
  STL: "STL",
  FBX: "FBX"
};
class FileStore {
  sceneStore = null;
  textureBakingStore = null;
  renderingStore = null;
  meshStore = null;
  meshPaintingStore = null;
  materialStore = null;
  initStores(
    sceneStore,
    textureBakingStore,
    renderingStore,
    meshPaintingStore,
    materialStore,
    meshStore
  ) {
    this.sceneStore = sceneStore;
    this.textureBakingStore = textureBakingStore;
    this.renderingStore = renderingStore;
    this.meshPaintingStore = meshPaintingStore;
    this.materialStore = materialStore;
    this.meshStore = meshStore;
  }
  loadFile = async (file) => {
    if (!this.sceneStore) {
      console.error("File store is not initialized");
      return;
    }
    if (!workerService.computeService) {
      console.error("Compute service is not initialized");
      return;
    }
    const ext = file.name.match(/\.([a-z]+)/);
    let fileType = null;
    if (!ext || ext.length == 0) {
      console.error("File has no extension, should have .stl");
      return;
    }
    if (ext[1] == "stl") fileType = FileType.STL;
    else if (ext[1] == "fbx") fileType = FileType.FBX;
    if (!fileType) return console.error(`Unknown type: ${ext[1]}`);
    //For stl
    let fileContent = await this._readBinaryFile(file);
    if (!fileContent) {
      return console.error("could not parse file: " + file.name);
    }
    this.sceneStore.meshName = file.name.replace(/\.[^/.]+$/, ""); //remove extention
    let meshes = [];
    let skinnedMeshes = [];
    let rootBoneNodes = [];
    let otherObjects = [];
    if (fileType == FileType.STL) {
      let geometry = await workerService.computeService.parseSTL(fileContent);
      //TODO for later do it in a worker
      // The following call splits the mesh into connex parts
      let geometries = ImportBackend.processImportGeometry(geometry);
      for (const geometry of geometries) {
        meshes.push(new Mesh(geometry, new MeshBasicMaterial()));
      }
    } else {
      const fbxContent = await ImportBackend.loadFBX(fileContent);
      const fbxOutput = ImportBackend.processFBXModel(fbxContent);
      meshes = fbxOutput.meshes;
      skinnedMeshes = fbxOutput.skinnedMeshes;
      rootBoneNodes = fbxOutput.rootBoneNodes;
      otherObjects = fbxOutput.otherObjects;
    }
    for (const boneRootNode of rootBoneNodes) {
      this.sceneStore.addBoneRootNode(boneRootNode);
    }
    return {
      meshes: meshes,
      skinnedMeshes: skinnedMeshes,
      rootBoneNodes: rootBoneNodes,
      otherObjects: otherObjects
    };
  };
  _readBinaryFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target) resolve(e.target.result);
        else reject();
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsArrayBuffer(file);
    });
  };
  async bytesToBase64DataUrl(bytes, type = "application/octet-stream") {
    return await new Promise((resolve, reject) => {
      const reader = Object.assign(new FileReader(), {
        onload: () => resolve(reader.result),
        onerror: () => reject(reader.error)
      });
      reader.readAsDataURL(new File([bytes], "", { type }));
    });
  }
  async exportMeshesColorTextured(options = { scale: 1.0 }) {
    let files = new Array();
    let export_mesh_group = new Object3D();
    if (
      !this.sceneStore ||
      !this.textureBakingStore ||
      !this.renderingStore ||
      !this.meshPaintingStore ||
      !this.materialStore ||
      !this.meshStore
    ) {
      console.error("File store is not initialized");
      return;
    }
    for (let [uuid, meshColor] of this.meshStore.meshColors) {
      const material = this.materialStore.getDisplayMaterial(uuid);
      if (!(meshColor instanceof Mesh)) continue;
      let export_geometry = meshColor.geometry.clone();
      // Apply morph
      if (
        meshColor.morphTargetInfluences &&
        meshColor.morphTargetInfluences.length > 0
      ) {
        const morphedAttributes = BufferGeometryUtils$1.getMorphedAttributes(
          export_geometry,
          meshColor.morphTargetInfluences
        );
        BufferGeometryUtils$1.setBufferAttributeArray(
          export_geometry,
          "position",
          morphedAttributes.positions
        );
        BufferGeometryUtils$1.setBufferAttributeArray(
          export_geometry,
          "normal",
          morphedAttributes.normals
        );
      }
      // apply skinning
      if (meshColor instanceof SkinnedMesh) {
        const skinnedAttributes = BufferGeometryUtils$1.getPosedAttributes(
          export_geometry,
          meshColor.skeleton,
          meshColor.bindMatrix,
          meshColor.bindMatrixInverse
        );
        BufferGeometryUtils$1.setBufferAttributeArray(
          export_geometry,
          "position",
          skinnedAttributes.positions
        );
        BufferGeometryUtils$1.setBufferAttributeArray(
          export_geometry,
          "normal",
          skinnedAttributes.normals
        );
      }
      export_geometry.deleteAttribute("normal");
      let positionAttribute = export_geometry.getAttribute("position");
      // Make sure that matrix world is updated on the CPU part
      // CPU matrix can be desynchronized compared to what is sent to shaders
      meshColor.updateMatrixWorld(true);
      // apply rigid transform
      positionAttribute.applyMatrix4(meshColor.matrixWorld);
      // apply custom scale
      if (options.scale !== 1.0) {
        positionAttribute.applyMatrix4(
          new Matrix4().makeScale(options.scale, options.scale, options.scale)
        );
      }
      const export_mesh = new Mesh(
        export_geometry,
        new MeshBasicMaterial({ color: 0xffffff, map: material?.map })
      );
      export_mesh_group.add(export_mesh);
      // Get export texture as base64 string
      let textureDataURL = this.textureBakingStore.bakeExportTexture(uuid);
      if (!textureDataURL) continue;
      let texture_name =
        this.sceneStore.meshName + export_mesh.uuid + "_mesh_color_texture.png";
      if (material && material.map) material.map.name = texture_name;
      // We remove the dataURL header and keep only the image base64 data
      // then we push the file
      files.push({
        name: texture_name,
        raw: textureDataURL.split("base64,")[1],
        base64: true
      });
    }
    this.renderingStore.renderer.setSize(window.innerWidth, window.innerHeight);
    const exporter = new OBJMTLExporter();
    let expt = exporter.parse(
      export_mesh_group,
      this.sceneStore.meshName + "_mesh_color_object"
    );
    files.push({
      name: this.sceneStore.meshName + "_mesh_color_object.obj",
      raw: expt.obj,
      base64: false
    });
    files.push({
      name: this.sceneStore.meshName + "_mesh_color_object.mtl",
      raw: expt.mtl,
      base64: false
    });
    const zipContent = await zipFiles(files);
    return zipContent;
  }
}

/**
 * Store containing the camera used for rendering
 */
class CameraStore {
  // CORE DATA -------------------
  camera;
  constructor(camera) {
    this.camera = new Observable(camera);
  }
}

const DensityShader = {
  uniforms: {
    densityTexture: { value: null }
  },
  vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
        }`,
  fragmentShader: /* glsl */ `

        uniform sampler2D densityTexture;

        varying vec2 vUv;

        void main() {

            // /!\ density is stored in blue channel in our pipeline
            float density = texture2D( densityTexture, vUv ).b;
            vec3 density_color;
            vec3 very_high_density = vec3(1,0,0);
            vec3 high_density = vec3(1,1,0);
            vec3 mid_density = vec3(0,1,0);
            vec3 low_density = vec3(0,0,1.0);
            vec3 no_density = vec3(0,0,0.0);

            if(density > 10.0)
                density_color = mix(high_density,very_high_density,min((density-10.0)/20.0,1.0));
            else if (density > 5.0)
                density_color = mix(mid_density,high_density,min((density-5.0)/5.0,1.0));
            else if (density > 2.0)
                density_color = mix(low_density,mid_density,min((density-2.0)/3.0,1.0));
            else
                density_color = mix(no_density,low_density,density/2.0);

            gl_FragColor = vec4(density_color,1.0);

        }`
};
class DensityMaterial extends ShaderMaterial {
  constructor(parameters) {
    super({
      uniforms: UniformsUtils.clone(DensityShader.uniforms),
      vertexShader: DensityShader.vertexShader,
      fragmentShader: DensityShader.fragmentShader
    });
  }
}

const include_hacked_lights_fragment_begin = (() => {
  // Here we hack lights_fragment_begin to replace
  // part of it.
  const chunkToReplace = /* glsl */ `geometry.position = - vViewPosition;
geometry.normal = normal;
geometry.viewDir = \\( isOrthographic \\) \\? vec3\\( 0, 0, 1 \\) : normalize\\( vViewPosition \\);`;
  // next step is for easy debuggging in case things changes in next versions of THREE.js
  // We expect to find the 3 lines above one after the other in the chunk, starting from Rev 130
  if (ShaderChunk.lights_fragment_begin.search(chunkToReplace) === -1) {
    console.error(
      "Cannot overwrite lights_fragment_begin in current version of THREE.JS, the shader chunk does not look like expected. This code has been written for THREE Rev 130, may be broken with your revision of THREE. Expect a compilation error."
    );
  }
  return ShaderChunk.lights_fragment_begin.replace(
    new RegExp(chunkToReplace),
    `
        geometry.position = position;
        geometry.normal = normal;
        geometry.viewDir = normal;
        `
  );
})();
const include_hacked_lights_physical_fragment = (() => {
  // Here we hack lights_fragment_begin to replace
  // part of it.
  const chunkToReplace = /* glsl */ `float geometryRoughness = max\\( max\\( dxy.x, dxy.y \\), dxy.z \\);`;
  // next step is for easy debuggging in case things changes in next versions of THREE.js
  // We expect to find the 3 lines above one after the other in the chunk, starting from Rev 130
  if (ShaderChunk.lights_physical_fragment.search(chunkToReplace) === -1) {
    console.log(ShaderChunk.lights_physical_fragment);
    console.error(
      "Cannot overwrite lights_physical_fragment in current version of THREE.JS, the shader chunk does not look like expected. This code has been written for THREE Rev 130, may be broken with your revision of THREE. Expect a compilation error."
    );
  }
  return ShaderChunk.lights_physical_fragment.replace(
    new RegExp(chunkToReplace),
    `
        float geometryRoughness = 0.0;
        `
  );
})();
const include_hacked_lights_physical_pars_fragment = (() => {
  const AreaLightChunkToReplace = /* glsl */ `vec3 viewDir = geometry.viewDir;`;
  if (
    ShaderChunk.lights_physical_pars_fragment.search(
      AreaLightChunkToReplace
    ) === -1
  ) {
    console.log(ShaderChunk.lights_physical_pars_fragment);
    console.error(
      "Cannot overwrite lights_physical_pars_fragment in current version of THREE.JS, the shader chunk does not look like expected. This code has been written for THREE Rev 130, may be broken with your revision of THREE. Expect a compilation error."
    );
  }
  // TODO try to make it work in the future
  //const SpecularChunkToReplace = /* glsl */`reflectedLight.directSpecular \\+= lightColor \\* fresnel \\* LTC\\_Evaluate\\( normal, viewDir, position, mInv, rectCoords \\);`
  //if (ShaderChunk.lights_physical_pars_fragment.search(SpecularChunkToReplace) === -1) {
  //    console.log(ShaderChunk.lights_physical_pars_fragment);
  //    console.error("Cannot overwrite lights_physical_pars_fragment in current version of THREE.JS, the shader chunk does not look like expected. This code has been written for THREE Rev 130, may be broken with your revision of THREE. Expect a compilation error.");
  //}
  return ShaderChunk.lights_physical_pars_fragment.replace(
    new RegExp(AreaLightChunkToReplace),
    `vec3 viewDir = normalize(geometry.position);`
  );
})();
const GradientPaintShader = {
  uniforms: {
    aoTexture: { value: null },
    ao_intensity: { value: 1.0 },
    ao_white_threshold: { value: 1.0 },
    colorGradient: {
      value: {
        lowColor: new Vector4(0, 0, 0, 1),
        lowColorLevel: 0.0,
        mediumColor: new Vector4(0.5, 0.5, 0.5, 1.0),
        mediumColorLevel: 0.5,
        highColor: new Vector4(1.0, 1.0, 1.0, 1.0),
        highColorLevel: 1.0
      }
    }
  },
  vertexShader: /* glsl */ `
        #define USE_UV
        attribute float meanCurvature;
        varying vec3 Fnormal;
        varying vec3 FWnormal;
        varying vec3 Fposition;
        varying float FmeanCurvature;
        #define STANDARD
        #include <common>
        #include <uv_pars_vertex>
        #include <uv2_pars_vertex>
        #include <morphtarget_pars_vertex>
        #include <skinning_pars_vertex>

        void main() {

            #include <skinbase_vertex>
            #include <uv_vertex>
            #include <begin_vertex>
            #include <morphtarget_vertex>
            #include <skinning_vertex>
            #include <project_vertex>
            vUv = uv;
            FWnormal = normalize((inverse(viewMatrix)*vec4(normalMatrix*normal,0.0)).xyz);
            // /!\ note that both positions and normals are in view space
            // this is only to make computation in fragment shader in the same space
            // also note that inverse(viewMatrix) is applied in fragment when computing
            // envmapping for instance
            // -> the results of this shader will match what will be baked
            Fnormal = normalMatrix * normal;
            Fposition = (modelViewMatrix * vec4( position, 1.0 )).xyz;
            FmeanCurvature = meanCurvature;
        }`,
  fragmentShader: /* glsl */ `
        #define USE_UV
        #define STANDARD



        //varying vec2 vUv;
        varying vec3 Fnormal;
        varying vec3 FWnormal;
        varying vec3 Fposition;
        varying float FmeanCurvature;

        uniform vec3 diffuse;
        uniform vec3 emissive;
        uniform float roughness;
        uniform float metalness;
        uniform float opacity;

        // @ColorBaker No sheen
        #ifdef USE_SHEEN
            uniform vec3 sheen;
        #endif



        #include <common>
        #include <packing>
        #include <dithering_pars_fragment>
        // #include <color_pars_fragment>
        #include <uv_pars_fragment>
        #include <uv2_pars_fragment>
        #include <map_pars_fragment>
        // #include <alphamap_pars_fragment>
        #include <aomap_pars_fragment>
        // #include <lightmap_pars_fragment>
        #include <emissivemap_pars_fragment>
        #include <bsdfs>
        // #include <transmission_pars_fragment>
        #include <cube_uv_reflection_fragment>
        #include <envmap_common_pars_fragment>
        #include <envmap_physical_pars_fragment>
        // #include <fog_pars_fragment>
        #include <lights_pars_begin>
        //#include <lights_physical_pars_fragment>
        ${include_hacked_lights_physical_pars_fragment}
        // #include <shadowmap_pars_fragment>
        // #include <bumpmap_pars_fragment>
        // #include <normalmap_pars_fragment>
        // #include <clearcoat_pars_fragment>
        #include <roughnessmap_pars_fragment>
        #include <metalnessmap_pars_fragment>
        // #include <logdepthbuf_pars_fragment>
        // #include <clipping_planes_pars_fragment>
        struct TripletColorGradient
        {
            vec4 lowColor;
            float lowColorLevel;
            vec4 mediumColor;
            float mediumColorLevel;
            vec4 highColor;
            float highColorLevel;
        };

        uniform sampler2D aoTexture;
        uniform float ao_intensity;
        uniform float ao_white_threshold;
        uniform bool selected;
        uniform bool hovered;
        uniform TripletColorGradient colorGradient;


        vec4 meshColorSurfaceGradientColoring(TripletColorGradient colorTriplet, float level)
        {
            vec4 mixedColor = vec4(1,1,1,1);
            if(level <= colorTriplet.lowColorLevel)
            {
                return colorTriplet.lowColor;
            }
            else if(level >= colorTriplet.highColorLevel)
            {
                return colorTriplet.highColor;
            }
            else if(level <= colorTriplet.mediumColorLevel)
            {
                float sumBar = (colorTriplet.lowColor.a + colorTriplet.mediumColor.a);
                float levelLowBar = sumBar > 0.0 ? colorTriplet.lowColor.a / sumBar : 0.0;
                float levelMediumBar = sumBar > 0.0 ? colorTriplet.mediumColor.a /sumBar : 0.0;
                float levelFactBar =  (levelMediumBar - levelLowBar);

                float mixFact = (level - colorTriplet.lowColorLevel)/(colorTriplet.mediumColorLevel - colorTriplet.lowColorLevel);
                float alphaMixFact = levelFactBar > 0.0 ? (1.0-levelFactBar*mixFact)*mixFact + levelFactBar*mixFact
                                : (1.0+levelFactBar)*mixFact - levelFactBar*mixFact*mixFact;

                mixedColor = mix(colorTriplet.lowColor,colorTriplet.mediumColor,alphaMixFact);

            }
            else
            {
                float sumBar = (colorTriplet.mediumColor.a + colorTriplet.highColor.a);
                float levelMediumBar = sumBar > 0.0 ? colorTriplet.mediumColor.a / sumBar : 0.0;
                float levelHighBar = sumBar > 0.0 ? colorTriplet.highColor.a / sumBar : 0.0;
                float levelFactBar = (levelHighBar - levelMediumBar);

                float mixFact = (level - colorTriplet.mediumColorLevel)/(colorTriplet.highColorLevel - colorTriplet.mediumColorLevel);
                float alphaMixFact = levelFactBar > 0.0 ? (1.0-levelFactBar*mixFact)*mixFact + levelFactBar*mixFact
                : (1.0+levelFactBar)*mixFact - levelFactBar*mixFact*mixFact;


                mixedColor = mix(colorTriplet.mediumColor,colorTriplet.highColor,alphaMixFact);
            }

            return mixedColor;
        }

        vec4 alphaBlendColor(vec4 color1, vec4 color2)
        {
            if(color2.a == 0.0)
                return color1;

            vec4 alphaCol;
            float alpha = color2.a*(1.0 - color1.a) + color1.a;
            alphaCol.r = (color1.r*color1.a + color2.r*color2.a*(1.0- color1.a))/alpha;
            alphaCol.g = (color1.g*color1.a + color2.g*color2.a*(1.0- color1.a))/alpha;
            alphaCol.b = (color1.b*color1.a + color2.b*color2.a*(1.0- color1.a))/alpha;
            alphaCol.a = alpha;

            return alphaCol;
        }


        void main() {
            vec3 normal = normalize(Fnormal);
            vec3 position = Fposition;

            float ao = texture( aoTexture, vUv ).r;
            float ao_col = max(0.0,(1.0-(1.0-ao)*ao_intensity));
            ao_col = min(1.0, ao_col/ao_white_threshold);

            vec3 lighting_color = vec3(0,0,0);
            //Lighting
            vec4 surfaceGradient = meshColorSurfaceGradientColoring(colorGradient,FmeanCurvature);
            vec4 diffuseColor;
            {
                // #include <clipping_planes_fragment>


                ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );
                vec3 totalEmissiveRadiance = emissive;

                // #include <logdepthbuf_fragment>
                //#include <map_fragment>
                diffuseColor = vec4(surfaceGradient.rgb, opacity*surfaceGradient.a);
                #ifdef USE_MAP
                 	vec4 texelColor = texture(map,vUv); //
                    //texelColor = mapTexelToLinear( texelColor );
                 	diffuseColor = texelColor;//alphaBlendColor(texelColor,diffuseColor);
                #endif

                // @ColorBaker no need for color alpha map, alpha test
                // #include <color_fragment>
                // #include <alphamap_fragment>
                // #include <alphatest_fragment>
                #include <roughnessmap_fragment>
                #include <metalnessmap_fragment>
                // @ColorBaker we need to replace the normal computation
                // #include <normal_fragment_begin>
                vec3 geometryNormal = normal;


                // @ColorBaker no need for normal maps, clearcoat
                // #include <normal_fragment_maps>
                // #include <clearcoat_normal_fragment_begin>
                // #include <clearcoat_normal_fragment_maps>
                #include <emissivemap_fragment>

                // accumulation
                //#include <lights_physical_fragment>
                ${include_hacked_lights_physical_fragment}
                // @ColorBaker
                // we need to replace
                //    geometry.viewDir = ( isOrthographic ) ? vec3( 0, 0, 1 ) : normalize( vViewPosition );
                // in lights_fragment_begin
                // as the view dire needs to be computed based on the normal if we want to get what is expected.
                ${include_hacked_lights_fragment_begin}
                //#include <lights_fragment_begin>
                #include <lights_fragment_maps>
                #include <lights_fragment_end>

                // modulation
                //#include <aomap_fragment>
                //AO
                reflectedLight.indirectDiffuse *= ao_col;
                #if defined( USE_ENVMAP ) && defined( STANDARD )
                 		float dotNV = saturate( dot( geometry.normal, geometry.viewDir ) );
                 		reflectedLight.indirectSpecular *= computeSpecularOcclusion( dotNV, ao_col, material.specularRoughness );
                #endif

                vec3 totalDiffuse = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse;
                vec3 totalSpecular = reflectedLight.directSpecular + reflectedLight.indirectSpecular;

                // #include <transmission_fragment>

                vec3 outgoingLight =  totalDiffuse + totalSpecular  + totalEmissiveRadiance;
                lighting_color = outgoingLight;

            }

            gl_FragColor = vec4(lighting_color,1.0);

            //#include <tonemapping_fragment>
            //#include <encodings_fragment>
            //#include <dithering_fragment>
        }`
};

class GradientPaintMaterial extends ShaderMaterial {
  // Need to replicate all StandardMaterial properties except the one inherited from Material
  color = new Color(0xffffff); // diffuse
  roughness = 1.0;
  metalness = 0.0;
  map = null;
  lightMap = null;
  lightMapIntensity = 1.0;
  aoMap = null;
  aoMapIntensity = 1.0;
  emissive = new Color(0x000000);
  emissiveIntensity = 1.0;
  emissiveMap = null;
  // bumpMap: StandardParams['bumpMap'] = null;
  // bumpScale: NonNullable<StandardParams['bumpScale']> = 1;
  // normalMap: StandardParams['lightMap'] = null;
  // normalMapType: NonNullable<StandardParams['normalMapType']> = TangentSpaceNormalMap;
  // normalScale: NonNullable<StandardParams['normalScale']> = new Vector2(1, 1);
  // displacementMap: StandardParams['lightMap'] = null;
  // displacementScale: NonNullable<StandardParams['displacementScale']> = 1;
  // displacementBias: NonNullable<StandardParams['displacementBias']> = 0;
  roughnessMap = null;
  metalnessMap = null;
  alphaMap = null;
  envMap = null;
  envMapIntensity = 1.0;
  refractionRatio = 0.98;
  // Following wireframe props are missing types in three.js so we can't get them from StandardParams
  wireframeLinecap = "round";
  wireframeLinejoin = "round";
  flatShading = false;
  vertexTangents = false;
  isMeshStandardMaterial = true;
  constructor(parameters) {
    super({
      uniforms: UniformsUtils.merge([
        UniformsLib.common,
        UniformsLib.envmap,
        UniformsLib.aomap,
        UniformsLib.lightmap,
        UniformsLib.emissivemap,
        // UniformsLib.bumpmap,
        // UniformsLib.normalmap,
        // UniformsLib.displacementmap,
        UniformsLib.roughnessmap,
        UniformsLib.metalnessmap,
        // UniformsLib.fog,
        UniformsLib.lights,
        {
          emissive: { value: new Color(0x000000) },
          roughness: { value: 1.0 },
          metalness: { value: 0.0 },
          envMapIntensity: { value: 1 }, // temporary
          envMap: { value: null } // temporary
        },
        UniformsUtils.clone(GradientPaintShader.uniforms)
      ]),
      vertexShader: GradientPaintShader.vertexShader,
      fragmentShader: GradientPaintShader.fragmentShader
    });
    this.defines = { STANDARD: "" };
    // this.type = 'MeshStandardMaterial';
    this.wireframe = false;
    this.wireframeLinewidth = 1;
    this.morphTargets = false;
    this.morphNormals = false;
    // Need to set to true in order to have lights uniforms set properly
    this.lights = true;
    //this.toneMapped = false;
    this.setValues(parameters);
  }
}

/**
 * Store that owns the materials, material switcher and texture used for rendering and baking
 *
 */
class MaterialStore {
  // CORE DATA ---------------
  wireframe;
  // COMPUTED -----------------
  meshMaterialProperties;
  debugMeshMaterialProperties;
  shadingBakingProperties;
  paintingMeshTextures;
  renderingProperties;
  materialSwitcher;
  // used to assign a different id to each material (useful for selection for instance)
  meshId = 1; // always incremented when a mesh is added
  meshStore;
  meshPaintingStore;
  bakingNeedsUpdateMeshes;
  // SIGNAL ------------------
  bakingTexturesPropertyAdded;
  bakingTexturesPropertyRemoved;
  constructor(meshStore, sceneStore, meshPaintingStore) {
    this.meshMaterialProperties = new Map();
    this.debugMeshMaterialProperties = new Map();
    this.shadingBakingProperties = new Map();
    this.paintingMeshTextures = new Map();
    this.renderingProperties = new Map();
    this.meshStore = meshStore;
    this.materialSwitcher = new SceneMaterialSwitcher(sceneStore.getScene());
    sceneStore.sceneChanged.on((scene) => {
      this.materialSwitcher.setScene(scene);
    });
    this.materialSwitcher.correspondancePolicy =
      MaterialCorrespondancePolicy.AlwaysFill;
    this.bakingTexturesPropertyAdded = new Signal();
    this.bakingTexturesPropertyRemoved = new Signal();
    this.bakingNeedsUpdateMeshes = new Set();
    this.wireframe = new Observable(false);
    this.wireframe.onChanged((wireframe) => {
      for (const [uuid, matProp] of this.meshMaterialProperties) {
        matProp.materialDensityDisplay.wireframe = wireframe;
        matProp.materialDensityDisplay.needsUpdate = true;
        matProp.gradientPaintDisplayMaterial.wireframe = wireframe;
        matProp.gradientPaintDisplayMaterial.needsUpdate = true;
      }
    });
    this.meshPaintingStore = meshPaintingStore;
    meshPaintingStore.propertyAdded.on((data) => {
      this.addMeshColorMaterialProperty(data);
    });
    meshPaintingStore.propertyRemoved.on((uuid) => {
      this.removeMeshColorProperties(uuid);
    });
  }
  static getBakingTextureNb() {
    // get number of field in type, not sure it is possible
    return 3;
  }
  setShadingMaterialUniformsSetup(material, property) {
    // Signals handling
    material.uniforms["ao_intensity"].value = property.aoIntensity.value;
    material.uniforms["ao_white_threshold"].value =
      property.aoWhiteThreshold.value;
    material.roughness = property.roughness.value;
    material.metalness = property.metalness.value;
    material.envMapIntensity = property.envMapIntensity.value;
    property.aoIntensity.onChanged((aoIntensity) => {
      material.uniforms["ao_intensity"].value = aoIntensity;
    });
    property.aoWhiteThreshold.onChanged((aoWhiteThreshold) => {
      material.uniforms["ao_white_threshold"].value = aoWhiteThreshold;
    });
    property.roughness.onChanged((roughness) => {
      material.roughness = roughness;
    });
    property.metalness.onChanged((metalness) => {
      material.metalness = metalness;
    });
    property.envMapIntensity.onChanged((envMapIntensity) => {
      material.envMapIntensity = envMapIntensity;
    });
  }
  setShadingMaterialPaintSetup(material, uuid) {
    const paintingMeshTexture = this.paintingMeshTextures.get(uuid);
    if (!paintingMeshTexture) {
      console.warn("Mesh :" + uuid + " has no painting properties assigned;");
      return;
    }
    // type problem in three-js-baking should be Texture | null
    material.map = paintingMeshTexture.getTexture();
    paintingMeshTexture.textureReallocated.on(() => {
      material.map = paintingMeshTexture.getTexture();
    });
  }
  addMeshColorMaterialProperty(property) {
    let meshColorMaterialProperties = {
      gradientPaintDisplayMaterial: new GradientPaintMaterial({
        roughness: property.meshPaintingProperties.roughness.value,
        metalness: property.meshPaintingProperties.metalness.value,
        envMapIntensity: property.meshPaintingProperties.envMapIntensity.value
      }),
      materialDisplay: new MeshBasicMaterial({ color: 0xffffff }),
      materialDensityDisplay: new DensityMaterial(),
      meshIdMaterial: idMaterial.clone()
    };
    this.setShadingMaterialUniformsSetup(
      meshColorMaterialProperties.gradientPaintDisplayMaterial,
      property.meshPaintingProperties
    );
    meshColorMaterialProperties.materialDisplay.wireframe =
      this.wireframe.value;
    meshColorMaterialProperties.gradientPaintDisplayMaterial.wireframe =
      this.wireframe.value;
    meshColorMaterialProperties.materialDensityDisplay.wireframe =
      this.wireframe.value;
    const meshColor = this.meshStore.getMeshColor(property.uuid);
    if (!meshColor) {
      throw "Material Store fatal error. adding material property to non existing MeshColor";
    }
    meshColor.material =
      meshColorMaterialProperties.gradientPaintDisplayMaterial;
    if (Object.keys(meshColor.geometry.morphAttributes).length > 0) {
      meshColorMaterialProperties.materialDisplay.morphTargets = true;
      meshColorMaterialProperties.gradientPaintDisplayMaterial.morphTargets = true;
    }
    this.meshStore.meshColorMorphTargetAdded.on((uuid) => {
      const materialProperty = this.meshMaterialProperties.get(uuid);
      if (
        materialProperty &&
        !materialProperty.gradientPaintDisplayMaterial.morphTargets
      ) {
        materialProperty.gradientPaintDisplayMaterial.morphTargets = true;
        materialProperty.gradientPaintDisplayMaterial.needsUpdate = true;
      }
    });
    meshColorMaterialProperties.meshIdMaterial.uniforms.id.value = ++this
      .meshId;
    this.meshMaterialProperties.set(property.uuid, meshColorMaterialProperties);
    //this.materialSwitcher.setMaterialCorrespondance(meshColorMaterialProperties.materialDisplay,"density",meshColorMaterialProperties.materialDensityDisplay);
    this.materialSwitcher.setObjOriginalMaterial(
      meshColor,
      meshColorMaterialProperties.gradientPaintDisplayMaterial
    );
    property.meshPaintingProperties.brushable.onChanged((brushable) => {
      if (brushable) {
        this.createPaintingTexture(property.uuid);
      } else {
        this.deletePaintingTexture(property.uuid);
      }
    });
    // Debug properties
    let debugBakedGeometryMaterial = null;
    const debugSamplingPointsMaterial = new MeshWorldNormalMaterial({
      vertexColors: false,
      morphTargets: true,
      wireframe: false
    }); /*new MeshBasicMaterial(
            {
                color: 0xff0000,
                vertexColors: false,
                morphTargets: true,
                wireframe: true
            });*/
    const debugBakedGeometryMesh = this.meshStore.debugBakedGeometryMeshes.get(
      property.uuid
    );
    if (debugBakedGeometryMesh) {
      debugBakedGeometryMaterial = new MeshBasicMaterial({
        color: 0xffffff,
        vertexColors: false,
        morphTargets: true,
        wireframe: true
      });
      this.meshStore.debugBakedGeometryMeshes.get(property.uuid).material =
        debugBakedGeometryMaterial;
    }
    this.meshStore.debugSamplingPointsMeshes.get(property.uuid).material =
      debugSamplingPointsMaterial;
    this.debugMeshMaterialProperties.set(property.uuid, {
      debugBakedGeometryMaterial: debugBakedGeometryMaterial,
      debugSamplingPointsMaterial: debugSamplingPointsMaterial
    });
    // Signals handling
    // baking must be triggered when colors and gradient changed
    property.meshPaintingProperties.colorGradient.onChanged((triplet) => {
      meshColorMaterialProperties.gradientPaintDisplayMaterial.uniforms[
        "colorGradient"
      ].value = {
        lowColor: new Vector4(
          triplet.lowColor.r,
          triplet.lowColor.g,
          triplet.lowColor.b,
          triplet.lowColorAlpha
        ),
        lowColorLevel: triplet.lowColorLevel,
        mediumColor: new Vector4(
          triplet.mediumColor.r,
          triplet.mediumColor.g,
          triplet.mediumColor.b,
          triplet.mediumColorAlpha
        ),
        mediumColorLevel: triplet.mediumColorLevel,
        highColor: new Vector4(
          triplet.highColor.r,
          triplet.highColor.g,
          triplet.highColor.b,
          triplet.highColorAlpha
        ),
        highColorLevel: triplet.highColorLevel
      };
      let paintingMeshTexture = this.paintingMeshTextures.get(property.uuid);
      let meshColor = this.meshStore.getMeshColor(property.uuid);
      if (meshColor && paintingMeshTexture) {
        paintingMeshTexture.updateMeshColorTextureFace(
          meshColor.getFaceColors()
        );
      }
    });
    property.meshPaintingProperties.colorsChanged.on((faces) => {
      let paintingMeshTexture = this.paintingMeshTextures.get(property.uuid);
      if (paintingMeshTexture) {
        paintingMeshTexture.updateMeshColorTextureFace(faces);
      }
    });
  }
  removeMeshColorProperties(uuid) {
    if (this.meshMaterialProperties.has(uuid)) {
      this.meshMaterialProperties.delete(uuid);
    }
    if (this.shadingBakingProperties.has(uuid)) {
      this.shadingBakingProperties.delete(uuid);
      this.bakingTexturesPropertyRemoved.emit(uuid);
    }
  }
  getBakingNeedsUpdateMeshes() {
    return this.bakingNeedsUpdateMeshes;
  }
  createPaintingTexture(uuid) {
    const meshColor = this.meshStore.getMeshColor(uuid);
    if (!meshColor) {
      console.warn("Mesh :" + uuid + " does not exist;");
      return;
    }
    const paintingMeshTexture =
      ComputeBackend.createMeshColorPaintTexture(meshColor);
    this.paintingMeshTextures.set(uuid, paintingMeshTexture);
    const shadingBakingProperties = this.shadingBakingProperties.get(uuid);
    if (shadingBakingProperties) {
      this.setShadingMaterialPaintSetup(
        shadingBakingProperties.shadingBakingMaterial,
        uuid
      );
      paintingMeshTexture.textureReallocated.on(() => {
        shadingBakingProperties.bakeRenderTarget.setSize(
          paintingMeshTexture.getTexture().image.width,
          paintingMeshTexture.getTexture().image.height
        );
      });
    }
    const meshMaterialProperties = this.meshMaterialProperties.get(uuid);
    if (meshMaterialProperties) {
      this.setShadingMaterialPaintSetup(
        meshMaterialProperties.gradientPaintDisplayMaterial,
        uuid
      );
    }
  }
  deletePaintingTexture(uuid) {
    const paintingMeshTexture = this.paintingMeshTextures.get(uuid);
    if (paintingMeshTexture) {
      paintingMeshTexture.dispose();
      this.paintingMeshTextures.delete(uuid);
      const meshMaterialProperties = this.meshMaterialProperties.get(uuid);
      if (meshMaterialProperties) {
        meshMaterialProperties.gradientPaintDisplayMaterial.map = null;
        meshMaterialProperties.gradientPaintDisplayMaterial.needsUpdate = true;
      }
      const shadingBakingProperties = this.shadingBakingProperties.get(uuid);
      if (shadingBakingProperties) {
        shadingBakingProperties.shadingBakingMaterial.map = null;
        shadingBakingProperties.shadingBakingMaterial.needsUpdate = true;
      }
    }
  }
  setMeshRenderingTexture(uuid, paintingMeshTexture, aoTexture) {
    let meshMaterialProperties = this.meshMaterialProperties.get(uuid);
    if (!meshMaterialProperties) {
      console.error("Trying to assign texture to empty materials");
      return;
    }
    this.paintingMeshTextures.set(uuid, paintingMeshTexture);
    let renderingProperty = this.renderingProperties.get(uuid);
    if (renderingProperty) {
      renderingProperty.aoTexture = aoTexture;
    } else {
      this.renderingProperties.set(uuid, { aoTexture: aoTexture });
    }
    this.setShadingMaterialPaintSetup(
      meshMaterialProperties.gradientPaintDisplayMaterial,
      uuid
    );
    meshMaterialProperties.gradientPaintDisplayMaterial.uniforms[
      "aoTexture"
    ].value = aoTexture.getTexture();
    aoTexture.textureReallocated.on(() => {
      meshMaterialProperties.gradientPaintDisplayMaterial.uniforms[
        "aoTexture"
      ].value = aoTexture.getTexture();
    });
  }
  getBakeRenderTarget(uuid) {
    let shadingBakingProperties = this.shadingBakingProperties.get(uuid);
    if (!shadingBakingProperties) {
      console.warn("Mesh :" + uuid + " has no baking texture assigned;");
      return null;
    }
    return shadingBakingProperties.bakeRenderTarget;
  }
  getDisplayMaterial(uuid) {
    let meshMaterialProperties = this.meshMaterialProperties.get(uuid);
    if (!meshMaterialProperties) {
      console.warn("Mesh :" + uuid + " has no material properties assigned;");
      return null;
    }
    //return meshMaterialProperties.materialDisplay;
    return meshMaterialProperties.gradientPaintDisplayMaterial;
  }
  getIdMaterial(uuid) {
    let meshMaterialProperties = this.meshMaterialProperties.get(uuid);
    if (!meshMaterialProperties) {
      console.warn("Mesh :" + uuid + " has no material properties assigned;");
      return null;
    }
    return meshMaterialProperties.meshIdMaterial;
  }
  getShadingBakingMaterial(uuid) {
    let shadingBakingProperties = this.shadingBakingProperties.get(uuid);
    if (!shadingBakingProperties) {
      console.warn("Mesh :" + uuid + " has no baking properties assigned;");
      return null;
    }
    return shadingBakingProperties.shadingBakingMaterial;
  }
  createBakingProperties(uuid) {
    const paintingMeshTexture = this.paintingMeshTextures.get(uuid);
    const meshPaintingProperties =
      this.meshPaintingStore.paintingPropertiesStore.get(uuid);
    const meshColor = this.meshStore.getMeshColor(uuid);
    const renderingProperty = this.renderingProperties.get(uuid);
    if (!meshColor) {
      console.warn("Mesh :" + uuid + " does not exist;");
      return;
    }
    if (!renderingProperty) {
      console.warn("Mesh :" + uuid + " has no rendering properties assigned;");
      return;
    }
    if (!meshPaintingProperties) {
      console.warn(
        "Mesh :" + uuid + " has no mesh painting properties assigned;"
      );
      return;
    }
    let shadingBakingProperties = this.shadingBakingProperties.get(uuid);
    if (!shadingBakingProperties) {
      // create beking material
      const shadingBakingMaterial = new STLBakerMaterial({
        roughness: meshPaintingProperties.roughness.value,
        metalness: meshPaintingProperties.metalness.value,
        envMapIntensity: meshPaintingProperties.envMapIntensity.value
      });
      this.setShadingMaterialUniformsSetup(
        shadingBakingMaterial,
        meshPaintingProperties
      );
      // create baking textures
      const textureWidth = renderingProperty.aoTexture.getTexture().image.width;
      const textureHeight =
        renderingProperty.aoTexture.getTexture().image.height;
      const normalTexture = new MeshColorTexture(meshColor);
      const positionTexture = new MeshColorTexture(meshColor);
      normalTexture.initialize();
      positionTexture.initialize();
      ComputeBackend.computeMeshColorNormalTexture(normalTexture, meshColor);
      ComputeBackend.computeMeshColorPositionTexture(
        positionTexture,
        meshColor
      );
      // set baking material texture uniforms
      if (paintingMeshTexture)
        this.setShadingMaterialPaintSetup(shadingBakingMaterial, uuid);
      shadingBakingMaterial.uniforms["positionTexture"].value =
        positionTexture.getTexture();
      positionTexture.textureReallocated.on(() => {
        shadingBakingMaterial.uniforms["positionTexture"].value =
          positionTexture.getTexture();
      });
      shadingBakingMaterial.uniforms["normalTexture"].value =
        normalTexture.getTexture();
      normalTexture.textureReallocated.on(() => {
        shadingBakingMaterial.uniforms["normalTexture"].value =
          normalTexture.getTexture();
      });
      shadingBakingMaterial.uniforms["aoTexture"].value =
        renderingProperty.aoTexture.getTexture();
      renderingProperty.aoTexture.textureReallocated.on(() => {
        shadingBakingMaterial.uniforms["aoTexture"].value =
          renderingProperty.aoTexture.getTexture();
      });
      // create baking render target
      let bakeRenderTarget = new WebGLRenderTarget(textureWidth, textureHeight);
      bakeRenderTarget.texture.minFilter = LinearFilter;
      bakeRenderTarget.texture.magFilter = LinearFilter;
      shadingBakingProperties = {
        normalTexture: normalTexture,
        positionTexture: positionTexture,
        shadingBakingMaterial: shadingBakingMaterial,
        bakeRenderTarget: bakeRenderTarget
      };
      if (paintingMeshTexture) {
        paintingMeshTexture.textureReallocated.on(() => {
          bakeRenderTarget.setSize(
            paintingMeshTexture.getTexture().image.width,
            paintingMeshTexture.getTexture().image.height
          );
        });
      }
      this.shadingBakingProperties.set(uuid, shadingBakingProperties);
      this.bakingTexturesPropertyAdded.emit({
        uuid: uuid,
        bakingTexturesProperty: shadingBakingProperties
      });
    }
  }
  deleteBakingProperties(uuid) {
    const shadingBakingProperty = this.shadingBakingProperties.get(uuid);
    if (shadingBakingProperty) {
      shadingBakingProperty.normalTexture.dispose();
      shadingBakingProperty.positionTexture.dispose();
      shadingBakingProperty.bakeRenderTarget.dispose();
      shadingBakingProperty.shadingBakingMaterial.dispose();
      this.shadingBakingProperties.delete(uuid);
    }
  }
  clearBakingProperties() {
    for (let [uuid, textures] of this.shadingBakingProperties) {
      this.deleteBakingProperties(uuid);
    }
  }
  clear() {
    this.materialSwitcher.clearMaterialCorrespondances();
    for (let [uuid, materials] of this.meshMaterialProperties) {
      materials.materialDensityDisplay.dispose();
      materials.meshIdMaterial.dispose();
      materials.gradientPaintDisplayMaterial.dispose();
    }
    for (let [uuid, materials] of this.debugMeshMaterialProperties) {
      if (materials.debugBakedGeometryMaterial)
        materials.debugBakedGeometryMaterial.dispose();
      materials.debugSamplingPointsMaterial.dispose();
    }
    this.clearBakingProperties();
    for (let [uuid, textures] of this.paintingMeshTextures) {
      textures.dispose();
    }
    for (let [uuid, property] of this.renderingProperties) {
      property.aoTexture.dispose();
    }
    this.meshMaterialProperties.clear();
    this.debugMeshMaterialProperties.clear();
    this.shadingBakingProperties.clear();
  }
}

/**
 * Store that contains selectors used to select which color sample to paint on
 */
class SelectionStore {
  // COMPUTED -----------------------
  vertexSelectors;
  meshStore;
  // Accessors
  vertexSelectorsMeshes;
  constructor(meshStore) {
    this.meshStore = meshStore;
    this.vertexSelectors = new Map();
    this.vertexSelectorsMeshes = new Map();
    this.meshStore.meshColorAdded.on((meshColor) =>
      this.addSelector(meshColor)
    );
    this.meshStore.meshColorRemoved.on((uuid) => this.removeSelector(uuid));
  }
  addSelector(meshColor) {
    let selector = new VertexSelector();
    this.vertexSelectors.set(meshColor.name, selector);
    this.vertexSelectorsMeshes.set(selector, meshColor.name);
    selector.setGeometry(meshColor.getColorSampleGeometry());
    if (!(meshColor instanceof MeshColorSkinned)) {
      selector.setMatrixTransform(meshColor.matrixWorld);
    }
    meshColor.samplesResolutionChanged.on(() => {
      selector.setGeometry(meshColor.getColorSampleGeometry());
    });
  }
  removeSelector(uuid) {
    if (this.vertexSelectors.has(uuid)) {
      this.vertexSelectors.delete(uuid);
    }
  }
  getSelector(uuid) {
    return this.vertexSelectors.get(uuid);
  }
  getSelectorMeshUUID(selector) {
    return this.vertexSelectorsMeshes.get(selector);
  }
  resetSelectorsSelection() {
    for (let selector of this.vertexSelectors.values())
      selector.resetSelection();
  }
  clear() {
    this.vertexSelectors.clear();
    this.vertexSelectorsMeshes.clear();
  }
}

/**
 * Store that owns all meshColors of the scene and
 * stores the painting groups each meshColor is part of.
 * These groups can be used for various painting operation such
 * as filling all meshes part of one group.
 * /!\ it is important to note that all meshes should have a unique name
 * accross the model
 */
class MeshStore {
  // CORE DATA----------------------------------
  meshColors;
  meshColorGroups;
  // COMPUTED ----------------------------------------
  // name of the displayed model
  meshName;
  debugBakedGeometryMeshes;
  debugSamplingPointsMeshes;
  // Accessors
  groupMeshAcc;
  //SIGNALS ---------------------------------
  meshColorAdded;
  meshColorRemoved;
  meshColorMorphTargetAdded;
  constructor() {
    this.meshColorAdded = new Signal();
    this.meshColorRemoved = new Signal();
    this.meshColorMorphTargetAdded = new Signal();
    this.meshName = "";
    this._init();
  }
  _init() {
    this.meshColorGroups = new Map();
    this.meshColors = new Map();
    this.debugBakedGeometryMeshes = new Map();
    this.debugSamplingPointsMeshes = new Map();
    this.groupMeshAcc = new Map();
  }
  clear() {
    this._init();
  }
  /**
   *
   * @param meshColor
   * @param groupId
   * @returns
   */
  addMeshColor(meshColor, groupId) {
    // Set meshColor name if no name was set before
    // This will be used when saving colors for ie as color files name should match
    if (!meshColor.name || meshColor.name == "") {
      //Generate uuid for meshColor
      let meshUUID = UUID.generate();
      meshColor.name = meshUUID;
    }
    // name should be unique
    // test
    if (this.meshColors.has(meshColor.name)) {
      console.error(
        "MeshColor " + meshColor.name + " already stored in the scene"
      );
      return meshColor.name;
    }
    this.meshColors.set(meshColor.name, meshColor);
    this.addMeshColorToGroup(meshColor, groupId);
    if (meshColor instanceof MeshColorSkinned)
      this.debugBakedGeometryMeshes.set(
        meshColor.name,
        new Mesh(meshColor.getSkinnedGeometry())
      );
    let colorSampleGeometry = meshColor.getColorSampleGeometry();
    if (colorSampleGeometry) {
      this.debugSamplingPointsMeshes.set(
        meshColor.name,
        new Mesh(colorSampleGeometry)
      );
    }
    this.meshColorAdded.emit(meshColor);
  }
  removeMeshColor(uuid) {
    if (this.meshColors.has(uuid)) {
      this.meshColors.delete(uuid);
      this.debugBakedGeometryMeshes.delete(uuid);
      this.meshColorRemoved.emit(uuid);
    }
  }
  addBlendShapeToMeshColor(uuid, geometry) {
    const meshColor = this.meshColors.get(uuid);
    if (meshColor) {
      if (!meshColor.geometry.morphAttributes.position)
        meshColor.geometry.morphAttributes.position = [];
      if (!meshColor.geometry.morphAttributes.normal)
        meshColor.geometry.morphAttributes.normal = [];
      meshColor.geometry.morphAttributes.position.push(
        new Float32BufferAttribute(geometry.getAttribute("position").array, 3)
      );
      meshColor.geometry.morphAttributes.normal.push(
        new Float32BufferAttribute(geometry.getAttribute("normal").array, 3)
      );
      meshColor.updateMorphTargets();
      this.meshColorMorphTargetAdded.emit(meshColor.name);
    }
  }
  getMeshColor(uuid) {
    return this.meshColors.get(uuid);
  }
  displayDebugBakedGeometryMeshes(show) {
    if (show) {
      for (const [uuid, mesh] of this.meshColors) {
        if (mesh.parent) {
          let debugMesh = this.debugBakedGeometryMeshes.get(uuid);
          mesh.parent.add(debugMesh);
          debugMesh.updateMatrixWorld(true);
          debugMesh.updateWorldMatrix(false, true);
        }
      }
    } else {
      for (const [uuid, mesh] of this.meshColors) {
        if (mesh.parent)
          mesh.parent.remove(this.debugBakedGeometryMeshes.get(uuid));
      }
    }
  }
  displayDebugSamplingPointsMeshes(show) {
    if (show) {
      for (const [uuid, mesh] of this.meshColors) {
        if (mesh.parent) {
          let debugMesh = this.debugSamplingPointsMeshes.get(uuid);
          mesh.parent.add(debugMesh);
          debugMesh.updateMatrixWorld(true);
          debugMesh.updateWorldMatrix(false, true);
          mesh.visible = false;
        }
      }
    } else {
      for (const [uuid, mesh] of this.meshColors) {
        mesh.visible = true;
        if (mesh.parent)
          mesh.parent.remove(this.debugSamplingPointsMeshes.get(uuid));
      }
    }
  }
  addMeshColorToGroup(mesh, groupId) {
    if (!this.meshColorGroups.has(groupId)) {
      this.meshColorGroups.set(groupId, [mesh]);
    } else {
      this.meshColorGroups.get(groupId).push(mesh);
    }
    this.groupMeshAcc.set(mesh.name, groupId);
  }
  getGroupMeshColors(groupId) {
    return this.meshColorGroups.get(groupId);
  }
  getMeshColorGroup(meshColor) {
    let meshColorGroup = this.groupMeshAcc.get(meshColor.name);
    if (!meshColorGroup) {
      console.error(
        "MeshColor " + meshColor.name + " already stored in the scene"
      );
      return;
    }
    return meshColorGroup;
  }
  removeGroup(groupUUID) {
    let groupID = this.meshColorGroups.get(groupUUID);
    if (groupID) {
      for (const meshUUID of groupID) {
        this.groupMeshAcc.delete(meshUUID.name);
      }
      this.meshColorGroups.delete(groupUUID);
    }
  }
}

/**
 * Store containing the lights and envmap of the rendered scene
 */
class LightStore {
  // CORE DATA -------------------
  envMap;
  // TODO do we allow a light to be added twice ?
  // Do we use a map with names ?? (might be too restrive for lights)
  lights;
  // ----------------------------------------------------
  lightAdded;
  lightRemoved;
  constructor() {
    this.envMap = new Observable(null);
    this.lights = new Set();
    this.lightAdded = new Signal();
    this.lightRemoved = new Signal();
  }
  addLight(light) {
    if (!this.lights.has(light)) {
      this.lights.add(light);
      this.lightAdded.emit(light);
    }
  }
  getLights() {
    return this.lights;
  }
  removeLight(light) {
    if (this.lights.has(light)) {
      this.lights.delete(light);
      this.lightRemoved.emit(light);
    }
  }
}

class BrushStore {
  // CORE DATA ------------------------------
  brushRenderProperty;
  brushPresets;
  // Computed --------------------------------
  brushOkLabColorGradient;
  // Optimization Variables
  optVec3 = new Vector3();
  constructor() {
    this.brushRenderProperty = {
      roughness: 0.5,
      metalness: 0.5,
      envMapIntensity: 1.0,
      colorGradient: {
        lowColor: new Color(0, 0, 0),
        lowColorAlpha: 1.0,
        lowColorLevel: 0.0,
        mediumColor: new Color(0.5, 0.5, 0.5),
        mediumColorAlpha: 1.0,
        mediumColorLevel: 0.5,
        highColor: new Color(1, 1, 1),
        highColorAlpha: 1.0,
        highColorLevel: 1.0
      }
    };
    this.brushPresets = new Map();
    this.brushOkLabColorGradient = {
      lowColor: new Vector3(0, 0, 0),
      lowColorAlpha: 1.0,
      lowColorLevel: 0.0,
      mediumColor: new Vector3(0.5, 0.5, 0.5),
      mediumColorAlpha: 1.0,
      mediumColorLevel: 0.5,
      highColor: new Vector3(1, 1, 1),
      highColorAlpha: 1.0,
      highColorLevel: 1.0
    };
    /*this.brushColorGradient.onChanged((brushColorGradient) => {
            this.optVec3.set(brushColorGradient.lowColor.r,brushColorGradient.lowColor.g,brushColorGradient.lowColor.b)
            RGBToOklab(this.optVec3,this.brushOkLabColorGradient.lowColor);
            this.optVec3.set(brushColorGradient.mediumColor.r,brushColorGradient.mediumColor.g,brushColorGradient.mediumColor.b)
            RGBToOklab(this.optVec3,this.brushOkLabColorGradient.mediumColor);
            this.optVec3.set(brushColorGradient.highColor.r,brushColorGradient.highColor.g,brushColorGradient.highColor.b)
            RGBToOklab(this.optVec3,this.brushOkLabColorGradient.highColor);

            this.brushOkLabColorGradient.lowColorAlpha = brushColorGradient.lowColorAlpha;
            this.brushOkLabColorGradient.lowColorLevel = brushColorGradient.lowColorLevel;

            this.brushOkLabColorGradient.mediumColorAlpha = brushColorGradient.mediumColorAlpha;
            this.brushOkLabColorGradient.mediumColorLevel = brushColorGradient.mediumColorLevel;

            this.brushOkLabColorGradient.highColorAlpha = brushColorGradient.highColorAlpha;
            this.brushOkLabColorGradient.highColorLevel = brushColorGradient.highColorLevel;
        })*/
  }
  addPreset(presetName, brushProperty) {
    if (!this.brushPresets.has(presetName)) {
      this.brushPresets.set(presetName, brushProperty);
    }
  }
  setBrushAsPreset(presetName) {
    let brushPreset = this.brushPresets.get(presetName);
    if (!brushPreset) {
      //TODO warn ?
      return;
    }
    let presetGradient = brushPreset.colorGradient;
    this.brushRenderProperty.roughness = brushPreset.roughness;
    this.brushRenderProperty.metalness = brushPreset.metalness;
    this.brushRenderProperty.envMapIntensity = brushPreset.envMapIntensity;
    this.brushRenderProperty.colorGradient.lowColor.copy(
      presetGradient.lowColor
    );
    this.brushRenderProperty.colorGradient.mediumColor.copy(
      presetGradient.mediumColor
    );
    this.brushRenderProperty.colorGradient.highColor.copy(
      presetGradient.highColor
    );
    this.brushRenderProperty.colorGradient.lowColorAlpha =
      presetGradient.lowColorAlpha;
    this.brushRenderProperty.colorGradient.mediumColorAlpha =
      presetGradient.mediumColorAlpha;
    this.brushRenderProperty.colorGradient.highColorAlpha =
      presetGradient.highColorAlpha;
    this.brushRenderProperty.colorGradient.lowColorLevel =
      presetGradient.lowColorLevel;
    this.brushRenderProperty.colorGradient.mediumColorLevel =
      presetGradient.mediumColorLevel;
    this.brushRenderProperty.colorGradient.highColorLevel =
      presetGradient.highColorLevel;
  }
  toJSON() {}
  fromJSON() {}
}

class Model {
  meshStore = null;
  meshPaintingStore = null;
  sceneStore = null;
  cameraStore = null;
  textureBakingStore = null;
  fileStore = null;
  renderingStore = null;
  materialStore = null;
  selectionStore = null;
  lightStore = null;
  brushStore = null;
  version = 1;
  constructor() {}
  initStores(renderer, camera, scene) {
    this.meshStore = new MeshStore();
    this.lightStore = new LightStore();
    this.sceneStore = new SceneStore(this.meshStore, this.lightStore, scene);
    this.selectionStore = new SelectionStore(this.meshStore);
    this.meshPaintingStore = new MeshPaintingStore(
      this.meshStore,
      this.selectionStore
    );
    this.materialStore = new MaterialStore(
      this.meshStore,
      this.sceneStore,
      this.meshPaintingStore
    );
    this.cameraStore = new CameraStore(camera);
    this.renderingStore = new RenderingStore(
      renderer,
      this.sceneStore,
      this.cameraStore,
      this.materialStore
    );
    this.textureBakingStore = new TextureBakingStore(
      this.materialStore,
      this.meshStore,
      this.renderingStore,
      this.lightStore,
      this.meshPaintingStore
    );
    this.fileStore = new FileStore();
    this.brushStore = new BrushStore();
    this.fileStore.initStores(
      this.sceneStore,
      this.textureBakingStore,
      this.renderingStore,
      this.meshPaintingStore,
      this.materialStore,
      this.meshStore
    );
  }
  toJSON() {}
  fromJSON(json) {}
  clear() {
    this.meshStore?.clear();
    this.sceneStore?.clear();
    this.materialStore?.clear();
    this.meshPaintingStore?.clear();
    this.selectionStore?.clear();
  }
}

class ComputeBackend {
  // TODO should backend only be static ?
  static asyncVertexBaker = null;
  static AOScene = new Scene();
  static setAOMeshesBoundingSphere(meshes) {
    if (!this.asyncVertexBaker) {
      console.warn("[ComputeBackend]: asyncVertexBacker must be set");
      return;
    }
    let meshesAABB = new Box3();
    for (const mesh of meshes) {
      meshesAABB.expandByObject(mesh);
    }
    let meshesBoundingSphere = new Sphere();
    meshesAABB.getBoundingSphere(meshesBoundingSphere);
    this.asyncVertexBaker.setBoundingSphere(meshesBoundingSphere);
  }
  static initAOVertexBaker(model) {
    if (!model.renderingStore) {
      console.log("computeMeshColorAO: model must be initialized");
      return;
    }
    this.asyncVertexBaker = new AsyncVertexBaker(
      model.renderingStore.renderer,
      this.AOScene,
      true,
      {
        numberOfShadowMaps: 512,
        shadowMapResolution: 512,
        aoMapIntensity: 1.2
      }
    );
    window.onbeforeunload = () => {
      if (this.asyncVertexBaker) this.asyncVertexBaker.dispose();
    };
  }
  static async bakeAOAllAtOnce(meshes) {
    if (!this.asyncVertexBaker) {
      console.warn("[ComputeBackend]: asyncVertexBacker must be set");
      return;
    }
    this.setAOMeshesBoundingSphere(meshes);
    let keyGroups = new Array();
    let meshGroups = new Array();
    for (let mesh of meshes) {
      let keys = [];
      for (let attribute in mesh.geometry.attributes) {
        keys.push(attribute);
      }
      for (let morphAttribute in mesh.geometry.morphAttributes) {
        //name would be the same as attribute without _morph
        keys.push(morphAttribute + "_morph");
      }
      let foundKeys = false;
      for (let i = 0; i < keyGroups.length; ++i) {
        if (keyGroups[i].size != keys.length) continue;
        let missingKey = false;
        for (const key of keys) {
          if (!keyGroups[i].has(key)) {
            missingKey = true;
            break;
          }
        }
        if (!missingKey) {
          foundKeys = true;
          meshGroups[i].push(mesh);
        }
      }
      if (!foundKeys) {
        let newKeyGroup = new Set();
        for (const key of keys) newKeyGroup.add(key);
        keyGroups.push(newKeyGroup);
        meshGroups.push([mesh]);
      }
    }
    for (let meshGroup of meshGroups)
      await this.asyncVertexBaker.bakeAllAtOnce(meshGroup);
  }
  static isAOBakingInProgress() {
    return this.asyncVertexBaker && this.asyncVertexBaker.inProgress;
  }
  static async computeMeshColorsAO(meshesColor, model) {
    if (!model.sceneStore || !model.renderingStore) {
      console.log("computeMeshColorAO: model must be initialized");
      return;
    }
    if (!this.asyncVertexBaker) {
      this.initAOVertexBaker(model);
    }
    //
    // AO is baked for all meshes at once.
    //
    // meshes used for computing the AO
    let sampleMeshes = new Array();
    const normalMatrix = new Matrix3();
    // we need to ensure that meshColor.matrixWorld is up to date
    // because positions and normals will be baked
    for (let i = 0; i < meshesColor.length; ++i) {
      const meshColor = meshesColor[i];
      meshColor.updateWorldMatrix(true, true);
      meshColor.updateMatrixWorld(true);
    }
    for (let i = 0; i < meshesColor.length; ++i) {
      const meshColor = meshesColor[i];
      const colorSampleGeometry = meshColor.getColorSampleGeometry();
      if (!colorSampleGeometry || !meshColor.geometry) continue;
      const material = new MeshBasicMaterial({
        color: 0xffffff
        //vertexColors: true,
      });
      const sampleMesh = new Mesh(colorSampleGeometry.clone(), material);
      sampleMesh.castShadow = true;
      sampleMesh.receiveShadow = true;
      // TODO KEEP this code because it will be used for sample mesh
      /*let shadowMesh;
            if (meshColor instanceof MeshColorSkinned && meshColor.skeleton) {
              shadowMesh = new SkinnedMesh(meshColor.geometry.clone(), material);
              shadowMesh.bindMode = meshColor.bindMode;
              shadowMesh.bind(meshColor.skeleton, meshColor.bindMatrix);
            }
            else
            {
              shadowMesh = new Mesh(meshColor.geometry.clone(), material);
            }*/
      // We bake the positions and normals
      // as the hierarchy is lost during the ao process
      normalMatrix.getNormalMatrix(meshColor.matrixWorld);
      sampleMesh.geometry
        .getAttribute("position")
        .applyMatrix4(meshColor.matrixWorld);
      sampleMesh.geometry.getAttribute("normal").applyMatrix3(normalMatrix);
      this.AOScene.add(sampleMesh);
      sampleMeshes.push(sampleMesh);
    }
    await this.bakeAOAllAtOnce(sampleMeshes);
    for (let i = 0; i < sampleMeshes.length; ++i) {
      // sample ao is an array of vec3, we need only the first coordinate
      let sampleAoAttribute = sampleMeshes[i].geometry.getAttribute("ao");
      let meshColorAoAttributeArray = new Float32Array(
        sampleAoAttribute.array.length / 3
      );
      for (let i = 0; i < meshColorAoAttributeArray.length; i++) {
        meshColorAoAttributeArray[i] = sampleAoAttribute.array[3 * i];
      }
      let meshColorAoAttribute = new BufferAttribute(
        meshColorAoAttributeArray,
        1
      );
      meshesColor[i]
        .getColorSampleGeometry()
        .setAttribute("ao", meshColorAoAttribute);
      this.AOScene.remove(sampleMeshes[i]);
      sampleMeshes[i].geometry.dispose();
    }
    //TODO test
    if (this.asyncVertexBaker) {
      this.asyncVertexBaker.dispose();
      this.asyncVertexBaker = null;
    }
  }
  //meshColors are stored inside each meshTextures
  static setModelProperties(meshesTextureColor, model) {
    if (!model.meshStore || !model.textureBakingStore || !model.materialStore) {
      console.error(
        "[MeshColor] setModelProperties: model has not been initialized"
      );
      return;
    }
    //console.time("set mesh properties");
    for (let i = 0; i < meshesTextureColor.length; ++i) {
      const aoTex = meshesTextureColor[i].meshAoColorTexture;
      if (!aoTex || !meshesTextureColor[i].meshPaintColorTexture) {
        continue;
      }
      const meshColor = aoTex.getMeshColor();
      //meshProperties.meshPoints = meshColor.getColorSampleNb();
      //meshProperties.meshSurface = BufferGeometryComputations.computeSurface(
      //  meshColor.geometry
      //);
      model.meshStore.addMeshColor(
        meshColor,
        meshColor.geometry.userData["MeshColorGroupGeometry"]
      );
      model.materialStore.setMeshRenderingTexture(
        meshColor.name,
        meshesTextureColor[i].meshPaintColorTexture, // not null already checked
        aoTex
      );
    }
    //console.timeEnd("set mesh properties");
  }
  static computeMeshColorDensities(meshesColor) {
    for (let i = 0; i < meshesColor.length; ++i) {
      meshesColor[i].computeSamplingDensity();
    }
  }
  static computeMeshColorSampleGeometricProperties(meshColor) {
    if (meshColor.getColorSampleGeometry()) {
      meshColor.saveColorsToAttribute("savedColors");
      meshColor.setColorsFromGeometryAttribute(
        "meanCurvature",
        true,
        false,
        true,
        false,
        false
      );
      meshColor.interpolateSamplesFromVertexSamples(false, true, false, false);
      meshColor.meshColorsToAttribute("meanCurvature", { r: "g" });
      //TODO test to tranfer attribute into non indexed geom
      const edgeColors = meshColor.getEdgeColors();
      const faceColors = meshColor.getFaceColors();
      let i = 0;
      let indexedGeometricAttribute = meshColor
        .getColorSampleGeometry()
        ?.getAttribute("meanCurvature");
      if (!indexedGeometricAttribute) return;
      let nonIndexedColorArray = new Float32Array(faceColors.length * 3);
      for (let face of faceColors) {
        let v0 = face.edge_orientation[0]
          ? edgeColors[face.edge_ids[0]].vertex_ids[0]
          : edgeColors[face.edge_ids[0]].vertex_ids[1];
        let v1 = face.edge_orientation[1]
          ? edgeColors[face.edge_ids[1]].vertex_ids[0]
          : edgeColors[face.edge_ids[1]].vertex_ids[1];
        let v2 = face.edge_orientation[2]
          ? edgeColors[face.edge_ids[2]].vertex_ids[0]
          : edgeColors[face.edge_ids[2]].vertex_ids[1];
        nonIndexedColorArray[i] = indexedGeometricAttribute?.getX(v0);
        nonIndexedColorArray[i + 1] = indexedGeometricAttribute?.getX(v1);
        nonIndexedColorArray[i + 2] = indexedGeometricAttribute?.getX(v2);
        i += 3;
      }
      meshColor.geometry.setAttribute(
        "meanCurvature",
        new BufferAttribute(nonIndexedColorArray, 1)
      );
      meshColor.setColorsFromGeometryAttribute("savedColors", false);
    }
  }
  /**
   *
   * @param {MeshColor[]} meshesColor
   */
  static computeMeshColorGeometricProperties(meshesColor) {
    //Compute curvatures and adjust values
    let indexedGeometries = new Array();
    for (let i = 0; i < meshesColor.length; ++i) {
      const meshColor = meshesColor[i];
      // TODO getSkinnedIndexed if necessary
      const curvatureGeometry = meshColor.getIndexedGeometry();
      computeGeometryMeanCurvatures(curvatureGeometry);
      indexedGeometries.push(curvatureGeometry);
    }
    adjustCurvatureAttribute(indexedGeometries);
  }
  static computeMeshColorAttributeTexture(
    meshColorTexture,
    attributeName,
    meshColor,
    defaultSampleColor,
    toggleChannels
  ) {
    meshColor.saveColorsToAttribute("savedColors");
    meshColor.resetColorSamples(defaultSampleColor);
    meshColor.setColorsFromGeometryAttribute(
      attributeName,
      false,
      toggleChannels.red,
      toggleChannels.green,
      toggleChannels.blue,
      toggleChannels.alpha
    );
    meshColorTexture.updateMeshColorTexture(true);
    meshColor.setColorsFromGeometryAttribute("savedColors", false);
  }
  static computeMeshColorAoTexture(meshAoColorTexture, meshColor) {
    ComputeBackend.computeMeshColorAttributeTexture(
      meshAoColorTexture,
      "ao",
      meshColor,
      new MColor(0, 0, 0, 1),
      {
        red: true,
        green: true,
        blue: true,
        alpha: false
      }
    );
  }
  static computeMeshColorPositionTexture(meshPositionColorTexture, meshColor) {
    ComputeBackend.computeMeshColorAttributeTexture(
      meshPositionColorTexture,
      "position",
      meshColor,
      new MColor(0, 0, 0, 1),
      {
        red: true,
        green: true,
        blue: true,
        alpha: false
      }
    );
  }
  static computeMeshColorNormalTexture(meshNormalColorTexture, meshColor) {
    ComputeBackend.computeMeshColorAttributeTexture(
      meshNormalColorTexture,
      "normal",
      meshColor,
      new MColor(0, 0, 0, 1),
      {
        red: true,
        green: true,
        blue: true,
        alpha: false
      }
    );
  }
  static computeMeshColorPaintTexture(meshPaintColorTexture, meshColor) {
    meshColor.resetColorSamples(new MColor(0, 0, 0, 0));
    meshPaintColorTexture.updateMeshColorTexture(true);
  }
  static createMeshColorPaintTexture(meshColor) {
    let meshPaintColorTexture = new MeshColorTexture(meshColor);
    meshPaintColorTexture.initialize();
    return meshPaintColorTexture;
  }
  //Compute all meshColorTextures needed to paint and bake lighting
  static computeMeshColorTexturesData(meshColorTextures, meshColor) {
    ComputeBackend.computeMeshColorAoTexture(
      meshColorTextures.meshAoColorTexture,
      meshColor
    );
    ComputeBackend.computeMeshColorPaintTexture(
      meshColorTextures.meshPaintColorTexture,
      meshColor
    );
  }
  static computeMeshColorTextures(meshColor) {
    meshColor.setColorsFromGeometryAttribute("ao", false);
    let meshAoColorTexture = new MeshColorTexture(meshColor, {
      maxTextureSize: 8192,
      marginLength: 0,
      type: MeshColorTextureType.Float,
      format: MeshColorTextureFormat.Red
    });
    meshAoColorTexture.initialize();
    meshColor.resetColorSamples(new MColor(0, 0, 0, 0));
    let meshPaintColorTexture =
      ComputeBackend.createMeshColorPaintTexture(meshColor);
    return {
      meshAoColorTexture: meshAoColorTexture,
      meshPaintColorTexture: meshPaintColorTexture
    };
  }
  static computeMeshColoBakingTextures(meshColor) {
    meshColor.setColorsFromGeometryAttribute("position", false);
    let meshPositionColorTexture = new MeshColorTexture(meshColor);
    meshPositionColorTexture.initialize();
    meshColor.setColorsFromGeometryAttribute("normal", false);
    let meshNormalColorTexture = new MeshColorTexture(meshColor);
    meshNormalColorTexture.initialize();
    return {
      meshPositionColorTexture: meshPositionColorTexture,
      meshNormalColorTexture: meshNormalColorTexture
    };
  }
}

const model = new Model();

const PaintingMode = {
  Brush: "Brush",
  Erase: "Erase",
  Fill: "Fill"
};
class UIPaintingStore {
  // CORE DATA ------------
  paintingMode;
  brushStroke;
  addedFirstPt = false;
  lastBrushPtTime = Date.now();
  // OPTIMIZATION VARIABLES
  interpolatedPt = new Vector3();
  constructor() {
    this.paintingMode = new Observable(PaintingMode.Brush);
    this.brushStroke = new Array();
  }
  appendBrushPoint(point) {
    this.brushStroke.push(point);
  }
  resetBrushStroke() {}
}

class UISelectionStore {
  // UI CORE DATA
  currentProps = null;
  hoveredProps = null;
  hoveredMesh = null;
  hoveredGroup = null;
  activeVertexSelector = null; //selector of active mesh (used for filling for instance)
  activeMeshColor; //active mesh (used for filling for instance)
  lastBrushRenderProperty; //maps a mesh uuid to its last color gradient value (used for preview)
  meshStore;
  meshPaintingStore;
  selectionStore;
  constructor(meshPaintingStore, meshStore, selectionStore) {
    this.meshPaintingStore = meshPaintingStore;
    this.meshStore = meshStore;
    this.selectionStore = selectionStore;
    this.activeMeshColor = new Observable(null);
    this.activeMeshColor.subscribe((meshColor) => {
      if (!meshColor) this.activeVertexSelector = null;
      else this.setActiveVertexSelector(meshColor.name);
    });
    this.lastBrushRenderProperty = new Map();
  }
  resetHoveredGroup() {
    this.hoveredGroup = null;
  }
  getHoveredGroup() {
    return this.hoveredGroup;
  }
  setHoveredGroup(meshColorName) {
    let mesh = this.meshStore.getMeshColor(meshColorName);
    if (mesh) {
      let group = this.meshStore.getMeshColorGroup(mesh);
      if (group) this.hoveredGroup = group;
    }
  }
  resetHoveredProps() {
    this.hoveredProps = null;
  }
  getHoveredProps() {
    return this.hoveredProps;
  }
  setHoveredProps(meshColorName) {
    let props =
      this.meshPaintingStore.paintingPropertiesStore.get(meshColorName);
    if (props) this.hoveredProps = props;
  }
  resetHoveredMesh() {
    this.hoveredMesh = null;
  }
  getHoveredMesh() {
    return this.hoveredMesh;
  }
  setHoveredMesh(meshColorName) {
    let mesh = this.meshStore.getMeshColor(meshColorName);
    if (mesh) this.hoveredMesh = mesh;
  }
  resetCurrentProps() {
    this.currentProps = null;
  }
  getCurrentProps() {
    return this.currentProps;
  }
  setCurrentProps(meshColorName) {
    let props =
      this.meshPaintingStore.paintingPropertiesStore.get(meshColorName);
    if (props) this.currentProps = props;
  }
  getActiveVertexSelector() {
    return this.activeVertexSelector;
  }
  setActiveVertexSelector(meshColorName) {
    let selector = this.selectionStore.getSelector(meshColorName);
    if (selector) {
      this.activeVertexSelector = selector;
    }
  }
  getMeshLastBrushRenderProperty(meshColorName) {
    return this.lastBrushRenderProperty.get(meshColorName);
  }
  setMeshLastBrushRenderProperty(meshColorName, renderProperty) {
    this.lastBrushRenderProperty.set(meshColorName, renderProperty);
  }
}

class UIModel {
  paintingStore;
  selectionStore;
  constructor(model) {
    this.paintingStore = new UIPaintingStore();
    this.selectionStore = new UISelectionStore(
      model.meshPaintingStore,
      model.meshStore,
      model.selectionStore
    );
  }
}

function getCameraRayObjectsIntersection(
  objs,
  canvasOriginX,
  canvasOriginY,
  camera,
  renderer,
  recursive = true
) {
  let rect = renderer.domElement.getBoundingClientRect(); //model.renderingStore.renderer.domElement.getBoundingClientRect();
  let offsets = { left: rect.left, top: rect.top };
  let _canvasX;
  let _canvasY;
  if (canvasOriginX !== undefined && canvasOriginY !== undefined) {
    _canvasX = [canvasOriginX - offsets.left];
    _canvasY = [canvasOriginY - offsets.top];
  }
  let ray = getCameraRay(
    camera,
    _canvasX[0],
    _canvasY[0],
    rect.width,
    rect.height
  );
  return getObjectsIntersections(ray, objs, recursive);
}

export {
  CameraStore,
  ComputeBackend,
  ComputeService,
  CubeTextureToTextureCubeUV,
  FileStore,
  ImportBackend,
  LightStore,
  MColor,
  MaterialStore,
  MeshColorSerializer,
  MeshColorSkinned,
  MeshColorTexture,
  MeshColorTextureFormat,
  MeshColorTextureSerializer,
  MeshColorTextureType,
  MeshPaintingProperties,
  MeshPaintingStore,
  MeshStore,
  Model,
  PaintingMode,
  RenderingPipelineType,
  RenderingStore,
  SceneStore,
  TextureBakingStore,
  UIModel,
  UIPaintingStore,
  UISelectionStore,
  WorkerService,
  adjustCurvatureAttribute,
  arrayBufferToBase64,
  base64ToArrayBuffer,
  generatePointHash,
  getAABB,
  getCameraRayObjectsIntersection,
  getColorFromOkLabTriplet,
  getColorFromTriplet,
  getMeshColorFacesFromVertexSelection,
  idMaterial,
  meshColorColoring,
  meshColorOkLabSurfaceGradientColoring,
  meshColorSurfaceColoring,
  meshColorSurfaceGradientColoring,
  model,
  silhouetteMaterial,
  splitAndCreaseGeometry,
  stringFromArrayBuffer,
  workerService,
  zipFiles
};
